// src/pages/BootcampTechnologies.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  GlobeAltIcon,
  CodeBracketIcon,
  PlayCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  TrophyIcon,
  ClipboardDocumentListIcon,
  LockClosedIcon,
  BookOpenIcon,
  XMarkIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  CheckBadgeIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  VideoCameraIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon,
  PhotoIcon,
  SpeakerWaveIcon,
  CircleStackIcon,
  CommandLineIcon,
  ClipboardDocumentCheckIcon,
  RocketLaunchIcon,
  FireIcon as FireIconOutline,
  StarIcon as StarIconOutline,
  BoltIcon as BoltIconOutline,
} from "@heroicons/react/24/outline";
import {
  FireIcon as FireIconSolid,
  StarIcon as StarIconSolid,
  BoltIcon as BoltIconSolid,
  TrophyIcon as TrophyIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
} from "@heroicons/react/24/solid";
import API_BASE_URL from "../Config/config";
import SubscriptionStatus from './SubscriptionStatus';
import VoiceAssistant from './NewVoiceAssistant';
import { useVoiceAssistant } from '../hooks/useVoiceAssistant';
import { useTheme } from '../contexts/ThemeContext';
import { useTaskCompletion } from '../hooks/useTaskCompletion';
import { updateLearningConsistency } from "../utils/consistency";
import { UserContext } from "../BasicComponents/UserContext";
import AchievementSystem from "./AchievementSystem";
import AIMentorController from "./AIMentorController";
// Language selector removed - always English
// import TSGLanguageSelector from '../components/TSGLanguageSelector';
import { checkLLMTrialStatus } from '../utils/llmTrialManager';
import { hasFullAccess, redirectToPaymentIfNeeded } from '../utils/paymentStatus';
import VisualizationPreview from "./shared/VisualizationPreview";

/* ------------------------------ small utils ------------------------------ */
const GitHubIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
  </svg>
);
const PlayIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const TabButton = ({ active, onClick, children }) => (
  <button 
    onClick={onClick} 
    className={`px-4 py-2 rounded-xl text-sm font-bold transition-all border ${
      active 
        ? "bg-primary text-white border-primary shadow-lg shadow-primary/25" 
        : "bg-white text-gray-500 border-gray-200 hover:border-primary/50 hover:text-primary"
    }`}
  >
    {children}
  </button>
);
const Button = ({ children, onClick, className = "", variant = "primary", ...props }) => {
  const baseStyles = "px-6 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2";
  const variants = {
    primary: "bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/25",
    outline: "border-2 border-primary text-primary hover:bg-primary/5",
    secondary: "bg-secondary/10 text-primary hover:bg-secondary/20",
    contained: "bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/25"
  };
  return (
    <button onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};
const Field = ({ label, children }) => (<div><div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</div>{children}</div>);
const Info = ({ label, children, variant = "default" }) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const styles = {
    default: "rounded-xl border border-secondary/20 bg-bgMain/40 p-3 flex flex-col gap-1",
    primary: "rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2",
    success: "rounded-xl border border-green-500/20 bg-green-500/5 p-4 space-y-2"
  };
  const labelStyles = {
    default: "text-xs font-bold text-primary uppercase tracking-wider",
    primary: "text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-2",
    success: "text-xs font-bold text-green-600 uppercase tracking-wider flex items-center gap-2"
  };
  
  return (
    <div className={styles[variant] || styles.default}>
      <div className={labelStyles[variant] || labelStyles.default}>
        {variant === 'primary' && <DocumentTextIcon className="h-4 w-4" />}
        {variant === 'success' && <CheckCircleIcon className="h-4 w-4" />}
        {label}
      </div>
      <p className={`text-sm leading-relaxed ${variant === 'default' ? (isDark ? "text-gray-100" : "text-gray-800 font-medium") : (isDark ? "text-gray-100" : "text-gray-900 font-medium")}`}>
        {children}
      </p>
    </div>
  );
};

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const slugify = (s ="") =>
 s.toString().trim().toLowerCase()
 .replace(/&/g," and")
 .replace(/\+/g," plus")
 .replace(/[^a-z0-9]+/g,"-")
 .replace(/(^-|-$)+/g,"");

// Safely convert explanation values to strings (handles objects, arrays, etc.)
const safeToString = (value) => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(safeToString).join('. ');
  if (typeof value === 'object') {
    // If it's an explanation object, extract the most relevant field
    if (value.topicOverview) return safeToString(value.topicOverview);
    if (value.explanation) return safeToString(value.explanation);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

// Convert CloudFront/S3 URLs to proxy URLs to avoid CORS issues
const getProxiedVideoUrl = (url) => {
 if (!url) return url;
 
 // Check if URL is from CloudFront or S3
 const cloudfrontPattern = /https?:\/\/d1atm4dw3zgpab\.cloudfront\.net\//;
 const s3Pattern = /https?:\/\/.*\.s3\.(.*\.)?amazonaws\.com\//;
 
 if (cloudfrontPattern.test(url) || s3Pattern.test(url)) {
 // Convert to proxy URL
 return `${API_BASE_URL}/api/video/proxy?url=${encodeURIComponent(url)}`;
 }
 
 // Not a CloudFront/S3 URL, return as is
 return url;
};

// Maps slugified technology names → actual public/tasks folder names (case-sensitive on Linux/S3)
const TECH_FOLDER_MAP = {
  "java": "java",
  "javascript": "javascript",
  "sql": "sql",
  "swift": "swift",
  "kubernetes": "kubernetes",
  "mongodb": "MongoDB",
  "blockchain": "Blockchain",
  "rust": "Rust",
  "cpp": "Cpp",
  "dart": "Dart",
  "jenkins": "Jenkins",
  "git-and-github": "Git & GitHub",
  "generative-ai": "Generative AI",
  "artificial-intelligence": "Artificial Intelligence",
  "ai-and-robotics": "AI & Robotics",
  "autonomous-ai-agents": "Autonomous AI Agents",
  "edge-ai": "Edge AI",
  "quantum-computing": "Quantum Computing",
  "python": "Python",
  "aws": "AWS",
  "golang": "GoLang",
};

const techFolderSlug = (technology ="") => {
 const slug = slugify(technology);
 return TECH_FOLDER_MAP[slug] || technology || slug;
};

const origin = typeof window !=="undefined" ? window.location.origin :"";
const publicBase = `${origin.replace(/\/+$/,"")}/`;
const pub = (p) => {
 const path = String(p).startsWith("/") ? String(p).slice(1) : String(p);
 // URL-encode each path segment to handle spaces and special chars in folder names
 const encoded = path.split('/').map(seg => encodeURIComponent(seg)).join('/');
 return `${publicBase}${encoded}`;
};

/* --------------------------- local progress store --------------------------- */
const pKey = (userId, tech) => `progress_${userId}_${slugify(tech)}`;
function readProgress(userId, tech) {
 try {
 const raw = localStorage.getItem(pKey(userId, tech));
 if (!raw) return { completedTaskIds: [], reviewStateById: {} };
 const parsed = JSON.parse(raw);
 if (Array.isArray(parsed)) return { completedTaskIds: parsed, reviewStateById: {} };
 return {
 completedTaskIds: Array.isArray(parsed.completedTaskIds) ? parsed.completedTaskIds : [],
 reviewStateById: parsed.reviewStateById || {},
 };
 } catch {}
 return { completedTaskIds: [], reviewStateById: {} };
}
function writeProgress(userId, tech, obj) {
  try {
    localStorage.setItem(pKey(userId, tech), JSON.stringify(obj));
    // Dispatch event to notify other components (like CertificatePage) that progress has changed
    window.dispatchEvent(new CustomEvent('skillgenProgressUpdated', { detail: { userId, tech } }));
  } catch {}
}

/* ------------------------------ mentor chat LS ----------------------------- */
const mKey = (userId, tech) => `mentor_${userId}_${slugify(tech)}`;
const readChat = (userId, tech) => { try { return JSON.parse(localStorage.getItem(mKey(userId, tech)) ||"[]"); } catch { return []; } };
const writeChat = (userId, tech, msgs) => { try { localStorage.setItem(mKey(userId, tech), JSON.stringify(msgs)); } catch {} };

/* ------------------------------ backend calls ------------------------------ */
// Only user-context is fetched from backend (authz + identity + enrollment)
async function fetchTechUserContext(userId) {
  const sessionToken = localStorage.getItem('session_token');
  const headers = {
    'Content-Type': 'application/json'
  };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }
  // userId is passed in URL path and query param, not in headers (to avoid CORS issues)
  const res = await fetch(`${API_BASE_URL}/api/bootcamp/tech/user-context/${encodeURIComponent(userId)}?userId=${encodeURIComponent(userId)}`, {
    headers
  });
  const data = await res.json();
  if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data; // {ok, user:{name,...}, enrollment:{track,technology,experienceLevel}}
}
// Mock review + mentor endpoints (you already have them on backend)
async function reviewTaskWithAI({ userId, technology, experienceLevel, task, docFile, imgFile, comments, githubUrl, submissionType }) {
  const url = `${API_BASE_URL}/api/user-ai-review`;
  try {
    const sessionToken = localStorage.getItem('session_token');
    const fd = new FormData();
    fd.append("userId", userId);
    fd.append("bunchName", technology);
    fd.append("experienceLevel", experienceLevel);
    fd.append("taskId", task.id);
    fd.append("taskTitle", task.title);
    fd.append("expectedOutput", task.expectedOutput ||"");
    fd.append("useCase", task.useCase ||"");
    fd.append("futureUseCase", task.futureUseCase ||"");
    fd.append("subtopics", JSON.stringify(task.subtopics || []));
    fd.append("submissionType", submissionType ||"files");
    fd.append("githubUrl", githubUrl ||"");
    fd.append("comments", comments ||"");
    if (docFile) fd.append("docFile", docFile);
    if (imgFile) fd.append("imgFile", imgFile);

    const headers = {};
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }

    const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, { method:"POST", body: fd, headers });
 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data; // {ok, passed, score, feedback}
 } catch (err) {
 console.error("Technology reviewTaskWithAI error:", err);
 throw new Error(
 err?.message
 ? `Submission failed: ${err.message}`
 :"Submission failed. Please try again after verifying your files are correct."
 );
 }
}

/**
 * AI-powered Evidence Type Determination
 * Automatically determines what evidence is needed based on task type
 */
function determineEvidenceType(taskTopic, taskTitle = "", taskData = {}) {
  const topic = (taskTopic || taskTitle || "").toLowerCase();
  const description = (taskData.description || "").toLowerCase();
  const expectedOutput = (taskData.expectedOutput || "").toLowerCase();
  const combinedText = `${topic} ${description} ${expectedOutput}`;

  // Check for screenshot requirement in expected output
  const needsScreenshot = combinedText.includes("screenshot") || combinedText.includes("image") || combinedText.includes("terminal output");
  const needsGithub = combinedText.includes("repository") || combinedText.includes("github") || combinedText.includes("project") || combinedText.includes("api");

  // Hybrid case (Project + Screenshots)
  if (needsGithub && (needsScreenshot || combinedText.includes("document") || combinedText.includes("script"))) {
    return {
      type: "hybrid_project",
      methods: ["github", "files"],
      fields: ["screenshot", "document", "terminalOutput"],
      requiresGithub: true,
      requiresScreenshot: needsScreenshot,
      aiGuidance: "This project requires both code pushed to GitHub and additional evidence like screenshots or scripts."
    };
  }

  // Installation/Setup tasks → Screenshot + Terminal
  if (topic.includes("setup") || topic.includes("install") || topic.includes("environment") ||
      topic.includes("ide") || topic.includes("jdk") || topic.includes("configuration") || needsScreenshot) {
    
    // Even if it looks like installation, if expected output specifically says "repository", include GitHub
    if (needsGithub) {
      return {
        type: "hybrid_project",
        methods: ["github", "files"],
        fields: ["screenshot", "terminalOutput", "document"],
        requiresGithub: true,
        requiresScreenshot: true,
        aiGuidance: "Please provide a screenshot of your successful setup and push any configuration scripts to your GitHub repository."
      };
    }

    return {
      type: "installation",
      methods: ["screenshot", "terminal", "files"],
      fields: ["screenshot", "terminalOutput", "document"],
      requiresGithub: false,
      requiresScreenshot: true,
      aiGuidance: "Please provide a screenshot of your successful installation or terminal output showing the results."
    };
  }

  // Git/GitHub tasks → GitHub repo review
  if (topic.includes("git") || topic.includes("github") || topic.includes("version control") ||
      topic.includes("commit") || topic.includes("repository")) {
    return {
      type: "git",
      methods: ["github", "files"],
      fields: ["screenshot", "terminalOutput"],
      requiresGithub: true,
      requiresScreenshot: needsScreenshot,
      aiGuidance: "I'll review your commits and repository structure automatically. Ensure your work is pushed."
    };
  }

  // Theory/Concept tasks → AI Questions
  if (topic.includes("introduction") || topic.includes("theory") || topic.includes("concept") ||
      topic.includes("overview") || topic.includes("fundamentals") || topic.includes("basics")) {
    return {
      type: "theory",
      methods: ["questions"],
      requiresGithub: false,
      aiGuidance: "Let's discuss the concepts. I'll ask you questions to verify your understanding."
    };
  }

  // Small coding tasks → Built-in compiler or files
  if (topic.includes("variables") || topic.includes("loops") || topic.includes("operators") ||
      topic.includes("arrays") || topic.includes("strings") || topic.includes("functions")) {
    return {
      type: "small_code",
      methods: ["compiler", "files"],
      fields: ["screenshot", "document", "terminalOutput"],
      requiresGithub: false,
      aiGuidance: "You can use the built-in compiler or upload your code file for review."
    };
  }

  // Medium/Large coding tasks → GitHub
  if (topic.includes("project") || topic.includes("assignment") || topic.includes("application") ||
      topic.includes("system") || topic.includes("framework") || topic.includes("api") ||
      topic.includes("database") || topic.includes("spring") || topic.includes("hibernate") || topic.includes("mongodb") ||
      topic.includes("crud") || topic.includes("web") || topic.includes("server") || topic.includes("react") || topic.includes("java")) {
    return {
      type: "project",
      methods: ["github", "files"],
      fields: ["screenshot", "document", "sqlScript", "queryOutput", "commands", "terminalOutput"],
      requiresGithub: true,
      aiGuidance: "Push your code to GitHub. I'll review your implementation, code quality, and best practices."
    };
  }

  // Default → Allow both methods
  return {
    type: "general",
    methods: ["files", "github"],
    fields: ["screenshot", "document", "terminalOutput"],
    requiresGithub: false,
    aiGuidance: "Submit your work via files or GitHub repository."
  };
}

async function askMentorAPI({ userId, technology, experienceLevel, task, text, imgFile, history = [] }) {
  const url = `${API_BASE_URL}/api/bootcamp/tech/mentor`;
  try {
    const sessionToken = localStorage.getItem('session_token');
    const fd = new FormData();
    fd.append("userId", userId);
    fd.append("technology", technology);
    fd.append("experienceLevel", experienceLevel);
    fd.append("taskId", task?.id ||"");
    fd.append("taskTitle", task?.title || task?.topic ||"");
    fd.append("context", JSON.stringify({
    expectedOutput: task?.expectedOutput ||"",
    subtopics: task?.subtopics || [],
    description: task?.description ||"",
    }));
    fd.append("message", text ||"");
    fd.append("history", JSON.stringify(history.slice(-10).map(m => ({ role: m.role, text: m.text })))); // no images in history
    fd.append("technology", technology);
    if (imgFile) fd.append("image", imgFile);

    const headers = {};
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }

    const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, { method:"POST", body: fd, headers });
    const data = await res.json();
    if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  } catch (err) {
 const header = `Demo mentor (API unreachable):\n\n`;
 const tail = `\n\nConfigure /api/bootcamp/tech/mentor for real mentoring.`;
 return { ok: true, answer: header + (text ||"Share your exact error.") + tail, mock: true, error: String(err?.message || err) };
 }
}
async function generateTopicExplanationAPI({ userId, technology, experienceLevel, taskId, task, language = 'English', explainType, context }) {
  const url = `${API_BASE_URL}/api/explain`;
  
  try {
    const sessionToken = localStorage.getItem('session_token');
    const headers = {
      "Content-Type": "application/json",
      "user-id": userId
    };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }

    const body = {
      userId,
      enrollmentId: null,
      taskId: taskId || task?.id || task?.task_id || (task?.topic || task?.title),
      topic: task?.topic || task?.title || technology || "General Programming",
      experienceLevel,
      task,
      language,
      explainType, // Pass explainType if provided
      context // Pass additional context (mcq or evaluation)
    };

    const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.error || `HTTP ${res.status}`);
    }

    return await res.json();
  } catch (err) {
    console.error("AI Guidance API error:", err);
    throw err;
  }
}

async function verifyMyWorkAPI({ enrollmentId, taskId, evidence, experienceLevel, userId }) {
  const url = `${API_BASE_URL}/api/ai/verify-my-work`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

  try {
    const sessionToken = localStorage.getItem('session_token');
    const headers = {
      "Content-Type": "application/json",
      "user-id": userId
    };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }

    const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
      method: "POST",
      headers,
      signal: controller.signal,
      body: JSON.stringify({
        enrollmentId,
        taskId,
        evidence,
        experienceLevel,
        userId
      }),
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      throw new Error(errorData.error || `HTTP ${res.status}`);
    }

    return await res.json();
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      throw new Error("Evaluation is taking longer than expected. Please try again in a moment as the background process might still be running.");
    }
    console.error("Verify My Work API error:", err);
    throw err;
  }
}

async function visualizeOutputAPI({ userId, technology, experienceLevel, task, hint }) {
  const url = `${API_BASE_URL}/api/bootcamp/tech/visualize`;
  const sessionToken = localStorage.getItem('session_token');

  // Add timeout to prevent hanging requests
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  try {
    const headers = {
      "Content-Type": "application/json",
      "user-id": userId
    };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }

    const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
      method: "POST",
      headers,
 body: JSON.stringify({ userId, technology, experienceLevel, task, hint, taskDescription: task?.description || "", taskSubtopics: task?.subtopics || [], taskUseCase: task?.useCase || "" }),
 signal: controller.signal,
 });
 
 clearTimeout(timeoutId);

 // Handle 504 Gateway Timeout specifically
 if (res.status === 504) {
 throw new Error("The visualization is taking longer than expected. This might be due to high server load. Please try again in a moment.");
 }

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) {
 // Provide more helpful error messages
 if (res.status === 504) {
 throw new Error("The visualization request timed out. Please try again with a simpler request or try again later.");
 }
 throw new Error(data.error || `HTTP ${res.status}`);
 }
 return data;
 } catch (err) {
 clearTimeout(timeoutId);
 // Handle abort (timeout) errors
 if (err.name === 'AbortError') {
 throw new Error("Request timed out. The visualization is taking too long. Please try again.");
 }
 // Re-throw other errors
 throw err;
 }
}

async function generateVideoAPI({ userId, task, courseType, experienceLevel }) {
  const url = `${API_BASE_URL}/api/video/generate`;
  const sessionToken = localStorage.getItem('session_token');
  const headers = {
    "Content-Type": "application/json",
    "user-id": userId
  };
  if (sessionToken) {
    headers['Authorization'] = `Bearer ${sessionToken}`;
  }
  const res = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
    method:"POST",
    headers,
    body: JSON.stringify({
      userId, task, courseType, experienceLevel, duration: 75
    }),
  });

 // Check if response is HTML (error page) instead of JSON
 const contentType = res.headers.get("content-type");
 if (!contentType || !contentType.includes("application/json")) {
 const text = await res.text();
 throw new Error(`Server returned HTML instead of JSON. Status: ${res.status}. Response: ${text.substring(0, 200)}...`);
 }

 const data = await res.json();
 if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
 return data;
}

/* --------------------------- load JSON from public ------------------------- */
function mapTasks(technology, data) {
 let items = [];
 if (Array.isArray(data?.tasks)) items = data.tasks;
 else if (Array.isArray(data?.topics)) items = data.topics;
 else if (data && (data.topic || data.task_title)) items = [data];

 return items.map((it, i) => ({
 id: it.task_id || `${slugify(technology)}-${slugify(it.topic || it.task_title || `topic-${i + 1}`)}`,
 title: it.task_title || `Task: ${it.topic || `Topic ${i + 1}`}`,
 topic: it.topic || it.task_title || `Topic ${i + 1}`,
 description: it.task_description || it.description ||"",
 subtopics: Array.isArray(it.subtopics) ? it.subtopics : [],
 useCase: it.use_case || it.useCase ||"",
 futureUseCase: it.future_use_case || it.futureUseCase ||"",
 expectedOutput: it.expected_output || it.expectedOutput ||"",
 }));
}

async function loadTasksFromPublic(technology, experienceLevel) {
 const url = pub(`/tasks/${techFolderSlug(technology)}/${slugify(experienceLevel)}.json`);
 
 // Check if user is paid
 const storedUser = localStorage.getItem("user");
 let userId = null;
 try {
   const parsedUser = storedUser ? JSON.parse(storedUser) : null;
   userId = parsedUser?.userId || parsedUser?.id || parsedUser?.user_id || localStorage.getItem("user_id");
 } catch (err) {
   userId = localStorage.getItem("user_id");
 }

 const user = JSON.parse(localStorage.getItem("user") || "{}");
 const isMainPlatform = window.location.hostname === 'www.theskillgenie.com' || 
                        window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';
 
 const isStudentTenant = user && (user.role === 'student' && (user.tenant || user.college_id || !isMainPlatform));
 let isPaid = isStudentTenant;
 
 if (!isPaid && userId) {
    const { hasFullAccess } = await import('../utils/paymentStatus');
    isPaid = await hasFullAccess(userId);
 }

 const res = await fetch(url, { cache:"no-store" });
 if (!res.ok) throw new Error("Tasks file not found or invalid");
 const data = await res.json();
 const allTasks = mapTasks(technology, data);

  // If not paid, don't allow access (no free trial for bootcamp)
  if (!isPaid) {
     return [];
  }

 return allTasks;
}

async function loadResourcesFromPublic(technology, experienceLevel) {
 const url = pub(`/resources/${techFolderSlug(technology)}/${slugify(experienceLevel)}.json`);
 try {
 const res = await fetch(url, { cache:"no-store" });
 if (!res.ok) return {};
 const data = await res.json();
 return data?.resourcesByTaskId && typeof data.resourcesByTaskId ==="object"
 ? data.resourcesByTaskId
 : {};
 } catch {
 return {};
 }
}

/* -------------------------------- component ------------------------------- */

export default function BootcampTechnologies() {
  const { user } = useContext(UserContext);
  const userIdFromContext = user?.uid || user?.id || localStorage.getItem('userId');

  const [analytics, setAnalytics] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!userIdFromContext) return;
    updateLearningConsistency(userIdFromContext, 'OPEN_DASHBOARD', 'Bootcamp');

    const sessionToken = localStorage.getItem('session_token');
    const headers = {
      'Content-Type': 'application/json'
    };
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    fetch(`${API_BASE_URL}/api/user/analytics?userId=${encodeURIComponent(userIdFromContext)}`, {
      headers
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setAnalytics(data);
      })
      .catch(err => console.error('Failed to fetch analytics:', err));
  }, [userIdFromContext]);
 const { theme } = useTheme();
 const isDark = theme === "dark";

 const [loading, setLoading] = useState(true);
 const [notAllowed, setNotAllowed] = useState("");

 // null => bootstrapping;"" => bootstrapped but missing
 const [userId, setUserId] = useState(null);

   const [tasks, setTasks] = useState([]);
   const [completedTaskIds, setCompletedTaskIds] = useState([]);
   const [viewIndex, setViewIndex] = useState(0);

   const totalTasks = useMemo(() => tasks.length, [tasks]);
   const doneSet = useMemo(() => new Set(completedTaskIds), [completedTaskIds]);
   const completedCount = useMemo(() => {
     let c = 0;
     for (let i = 0; i < totalTasks; i++) {
       const id = tasks[i]?.id;
       if (id && doneSet.has(id)) c++;
       else break;
     }
     return c;
   }, [doneSet, tasks, totalTasks]);
   const unlockedIndex = useMemo(() => clamp(completedCount, 0, Math.max(0, totalTasks - 1)), [completedCount, totalTasks]);
   const percent = useMemo(() => totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0, [completedCount, totalTasks]);
   const viewingTask = useMemo(() => (viewIndex != null && tasks[viewIndex]) ? tasks[viewIndex] : null, [viewIndex, tasks]);

   const openTask = (idx, tab ="submit") => { 
     setViewIndex(idx); 
     setModalTab(tab); 
     setInlineTaskView(true); // Show inline instead of modal
     window.scrollTo({ top: 0, behavior: 'smooth' });
   };
   const openCurrentTask = (tab ="submit") => openTask(unlockedIndex, tab);

   // identity/enrollment from backend
 const { userContext, setUserContext } = useContext(UserContext);
 const [githubUrl, setGithubUrl] = useState("");
 const [submissionType, setSubmissionType] = useState("");
 const [ttsLanguage, setTtsLanguage] = useState('en-US');
 const [isSpeaking, setIsSpeaking] = useState(false);
 const ttsLanguages = [
   { code: 'en-US', name: 'English (US)' },
   { code: 'en-GB', name: 'English (UK)' },
   { code: 'hi-IN', name: 'Hindi' },
   { code: 'te-IN', name: 'Telugu' },
   { code: 'ta-IN', name: 'Tamil' },
   { code: 'kn-IN', name: 'Kannada' }
 ];
 const speakText = (text) => {
   if (!window.speechSynthesis) return;
   window.speechSynthesis.cancel();
   const utterance = new SpeechSynthesisUtterance(text);
   utterance.lang = ttsLanguage;
   utterance.onstart = () => setIsSpeaking(true);
   utterance.onend = () => setIsSpeaking(false);
   utterance.onerror = () => setIsSpeaking(false);
   window.speechSynthesis.speak(utterance);
 };
 const stopSpeaking = () => {
   if (window.speechSynthesis) {
     window.speechSynthesis.cancel();
     setIsSpeaking(false);
   }
 };

   const [userName, setUserName] = useState("");
   const [track, setTrack] = useState("");
   const [technology, setTechnology] = useState("");

   const [experienceLevel, setExperienceLevel] = useState("");
   const [githubRepoUrl, setGithubRepoUrl] = useState("");
   const [invitationStatus, setInvitationStatus] = useState("");

   const [resourcesByTaskId, setResourcesByTaskId] = useState({});
   const [reviewStateById, setReviewStateById] = useState({});
   const [certificateEligible, setCertificateEligible] = useState(false);
  
   // Task completion tracking
   const { markTaskCompleted } = useTaskCompletion(userId, 'technology');

   const [modalTab, setModalTab] = useState("submit");
 const [showModal, setShowModal] = useState(false); // We'll keep this for now but might remove later
 const [inlineTaskView, setInlineTaskView] = useState(false);
 const [showMentor, setShowMentor] = useState(false);
 const [mentorMessages, setMentorMessages] = useState([]);
 
 // 🎯 LLM Trial state
 const [llmTrialStatus, setLlmTrialStatus] = useState(null);
 const [showTrialModal, setShowTrialModal] = useState(false);
 const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
 
 // Voice Assistant
 const [vaInitialMessage, setVaInitialMessage] = useState("");
 const {
   isVoiceAssistantOpen,
   openVoiceAssistant,
   closeVoiceAssistant,
   handleVoiceCommand,
   handleVoiceNavigation
 } = useVoiceAssistant();

 // Helper to open Voice Assistant with subtopic explanation
 const explainWithAI = async (subtopic) => {
   try {
     // Instead of opening the voice assistant popup, we'll update the dashboard UI
     setInlineTaskView(true);
    
     setVaInitialMessage(`Generating explanation for: ${subtopic}...`);
     // openVoiceAssistant(); // Removed to avoid popup
    
     // Set loading state for the AI Mentor tab
     setExplanationLoading(true);
     setTopicExplanation(null);

     // Fetch detailed explanation for the subtopic
     const res = await fetch(`${API_BASE_URL}/api/explain`, {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
         'user-id': userId
       },
       body: JSON.stringify({
         userId,
         topic: subtopic,
         experienceLevel: experienceLevel || 'beginner',
         language: explanationLanguage,
         explainType: 'subtopic'
       }),
     });
    
     if (res.ok) {
       const data = await res.json();
       let explanationData = data.explanation?.explanation || data.explanation;
       if (typeof explanationData === 'string') {
         explanationData = { topicOverview: explanationData };
       }
       setTopicExplanation(explanationData);
     } else {
       setVaInitialMessage(`Sorry, I couldn't generate an explanation for "${subtopic}" right now. Please try again.`);
     }
   } catch (err) {
     console.error("Failed to explain subtopic:", err);
     setVaInitialMessage("Error connecting to AI Assistant.");
   } finally {
     setExplanationLoading(false);
   }
 };

   const [explanationLoading, setExplanationLoading] = useState(false);
   const [explanationGeneratedFor, setExplanationGeneratedFor] = useState(null); // Track which task ID we generated for
   const [topicExplanation, setTopicExplanation] = useState(null);
   const [githubStatus, setGithubStatus] = useState(null);
   const [explanationLanguage, setExplanationLanguage] = useState('en');
  const [explanationUsage, setExplanationUsage] = useState(null);
 const [videoUsage, setVideoUsage] = useState(null);
 const [videoGenerating, setVideoGenerating] = useState(false);
 const [videoUrl, setVideoUrl] = useState(null);
 const [videoStatus, setVideoStatus] = useState("idle"); // idle | generating | queued | ready | error
 const [videoMessage, setVideoMessage] = useState("");
 const [videoError, setVideoError] = useState("");
 const videoPollRef = useRef(null);
 const videoBaseUrlRef = useRef(null);
 const videoPollInFlightRef = useRef(false);
 const videoPollStartedAtRef = useRef(null);
 const videoErrorRetriesRef = useRef(0);
 const videoStatusRef = useRef(""); // Track video status in ref to avoid closure issues
 const [visualizing, setVisualizing] = useState(false);
 const [visualUrl, setVisualUrl] = useState("");
 const [visualGeneratedAt, setVisualGeneratedAt] = useState(null);
 const [visualHint, setVisualHint] = useState("");
 const [selectedLanguage] = useState('en'); // Always English - language selection removed

 // bootstrap user id from localStorage
 useEffect(() => {
 const id =
 localStorage.getItem("user_id") ||
 (() => {
 try {
 const u = JSON.parse(localStorage.getItem("user") ||"{}");
 return u.userId || u.user_id ||"";
 } catch {
 return"";
 }
 })();
 setUserId(id ||"");
 }, []);

 // Check payment status before loading dashboard
 useEffect(() => {
 let alive = true;
 (async () => {
 if (userId === null || !userId) return;
 
 try {
   // ✅ NEW: Student Tenant Access check first
   const user = JSON.parse(localStorage.getItem("user") || '{}');
   const isMainPlatform = window.location.hostname === 'theskillgenie.com' || 
                          window.location.hostname === 'www.theskillgenie.com' || 
                          window.location.hostname === 'localhost';

   if (user && (user.role === 'student' && (user.tenant || !isMainPlatform))) {
     console.log(`[TechnologyDashboard] Full access granted to Student Tenant: ${userId}`);
     setLoading(false);
     setNotAllowed("");
     return;
   }

 const hasAccess = await hasFullAccess(userId);
 if (!hasAccess && alive) {
 const proceed = await redirectToPaymentIfNeeded(userId, navigate);
 if (!proceed && alive) {
 setLoading(false);
 setNotAllowed("Premium subscription required to access this dashboard.");
 return;
 }
 }
 } catch (err) {
 console.error("Payment check failed:", err);
 // Continue loading - backend will also check
 }
 })();
 return () => { alive = false; };
 }, [userId, navigate]);

 // Re-pull the enrollment from user-context to refresh the invitation status
 // (updated server-side by the GitHub webhook once the invite is accepted).
 const syncInvitationStatus = async () => {
   if (!userId) return;
   try {
     const ctx = await fetchTechUserContext(userId);
     const enr = ctx?.enrollment;
     if (enr) {
       setInvitationStatus(enr.invitationStatus || "");
       setGithubRepoUrl(enr.githubRepoUrl || "");
       fetchGitHubStatus(enr);
     }
   } catch (err) {
     console.error("Failed to sync invitation status", err);
   }
 };

 // The repo details are already returned by /api/bootcamp/tech/user-context,
 // so derive the GitHub status panel from the enrollment instead of calling a
 // dedicated endpoint (which is not provisioned in the deployed backend).
 const fetchGitHubStatus = (enr) => {
   if (!enr?.githubRepoUrl) return;
   setGithubStatus({
     repo_name: enr.githubRepoUrl.replace(/\/+$/, "").split("/").pop(),
     repo_url: enr.githubRepoUrl,
     invite_status: enr.invitationStatus || "pending",
     updated_at: enr.updated_at || null,
   });
 };

 // load context (backend) → tasks/resources (public) → progress (LS)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (userId === null) return; // still bootstrapping
        if (!userId) { if (alive) { setLoading(false); setNotAllowed("Missing login."); } return; }
        if (alive) setNotAllowed("");

        const ctx = await fetchTechUserContext(userId);
        const fullName = ctx.user?.name || ctx.user?.userId || userId;
        const enr = ctx.enrollment; // backend already guarantees technologies-track

        if (alive) {
          setUserName(fullName);
          setTrack(enr.track);
          setTechnology(enr.technology || "");
          setExperienceLevel(enr.experienceLevel || "");
          setGithubRepoUrl(enr.githubRepoUrl || "");
          setInvitationStatus(enr.invitationStatus || "");
          if (enr.githubRepoUrl) {
            setGithubUrl(enr.githubRepoUrl);
            setSubmissionType("github");
            fetchGitHubStatus(enr);
          }
        }

        const [t, resMap] = await Promise.all([
          loadTasksFromPublic(enr.technology, enr.experienceLevel),
          loadResourcesFromPublic(enr.technology, enr.experienceLevel),
        ]);

        const store = readProgress(userId, enr.technology);
        if (alive) {
          setTasks(t);
          setResourcesByTaskId(resMap);
          setCompletedTaskIds(store.completedTaskIds);
          setReviewStateById(store.reviewStateById || {});
          setMentorMessages(readChat(userId, enr.technology));
          const eligible = t.length > 0 && (store.completedTaskIds?.length || 0) >= t.length;
          setCertificateEligible(eligible);
          
          // Set viewIndex to first incomplete task
          const doneSet = new Set(store.completedTaskIds);
          let firstIncomplete = 0;
          for (let i = 0; i < t.length; i++) {
            if (!doneSet.has(t[i].id)) {
              firstIncomplete = i;
              break;
            }
          }
          setViewIndex(firstIncomplete);

          // Store total tasks count for certificate center
          const totalTasksKey = `totalTasks_${enr.technology}_${enr.experienceLevel}`;
          localStorage.setItem(totalTasksKey, t.length.toString());
          console.log(`📊 Stored total tasks count: ${t.length} for course: ${enr.technology}`);
          
          setLoading(false);
        }
      } catch (err) {
        if (alive) {
          setNotAllowed(String(err?.message || "Access restricted"));
          setLoading(false);
        }
      }
    })();
    return () => { alive = false; };
  }, [userId]);

 // ✅ Check LLM Trial Status on component mount and auto-show welcome modal
 useEffect(() => {
 if (userId) {
 (async () => {
 const status = await checkLLMTrialStatus(userId);
 setLlmTrialStatus(status);
 console.log('🎯 LLM Trial Status (Technology):', status);
 
 // Auto-show welcome modal for new trial users (first time only)
 if (status?.hasActiveTrial && status?.source === 'trial') {
 const welcomeShownKey = `llm_trial_welcome_shown_${userId}`;
 const welcomeShown = localStorage.getItem(welcomeShownKey);
 if (!welcomeShown) {
 setShowTrialModal(true);
 localStorage.setItem(welcomeShownKey, 'true');
 }
 }
 })();
 }
 }, [userId]);

   const handleVerifyMyWork = async ({ 
    docFile, imgFile, comments, githubUrl, submissionType, setSubmitting, setServerError,
    sqlScript, queryOutput, commands, terminalOutput, evidenceInfo: providedEvidenceInfo
  }) => {
    try {
      setServerError("");
      setSubmitting(true);

      const taskId = viewingTask.id || viewingTask.task_id;
      const pendingMap = { ...reviewStateById, [taskId]: { status: "pending" } };
      writeProgress(userId, technology, { completedTaskIds, reviewStateById: pendingMap });
      setReviewStateById(pendingMap);

      // Auto-detect evidence requirements (fallback to provided if available)
      const evidenceInfo = providedEvidenceInfo || determineEvidenceType(viewingTask.topic, viewingTask.task_title, viewingTask);

      // Collect Evidence
      const evidence = {
        comments,
        sqlScript,
        queryOutput,
        commands,
        terminalOutput,
        submissionType: submissionType,
        taskType: viewingTask.task_type || "General"
      };

       // Auto-use provisioned GitHub repo for Git/Project tasks
       const isGithubTask = viewingTask.requires_github || viewingTask.task_type?.toLowerCase().includes('project') || evidenceInfo?.requiresGithub || submissionType === 'github';
       if (isGithubTask && githubRepoUrl) {
         evidence.githubUrl = githubRepoUrl;
         evidence.submissionType = "github";
         evidence.autoFetched = true; 
       } else if (githubRepoUrl || githubUrl) {
         evidence.githubUrl = githubRepoUrl || githubUrl;
         evidence.submissionType = "github";
       }

       if (imgFile) {
         // In a real production app, you'd upload this to S3 first
         // For now, we'll pass the file name or a placeholder if it's small enough,
         // but typically evidence collection would involve an upload step.
         evidence.screenshot = imgFile.name;
       }

       const result = await verifyMyWorkAPI({
         enrollmentId: userContext?.enrollment?.enrollment_id,
         taskId: taskId,
         evidence,
         experienceLevel,
         userId,
         taskMetadata: viewingTask
       });

       const feedback = result.feedback;

       // Task requires evidence the learner didn't provide — prompt for it
       // instead of recording a failed attempt.
       if (feedback?.needs_evidence || feedback?.status === "NEEDS_EVIDENCE") {
         const reverted = { ...reviewStateById };
         delete reverted[taskId];
         setReviewStateById(reverted);
         writeProgress(userId, technology, { completedTaskIds, reviewStateById: reverted });
         setServerError(feedback.message || "Please provide the required evidence for this task.");
         return;
       }

       const passed = feedback.score >= 0.7; // Example threshold

       const nextReview = { 
         status: passed ? "passed" : "failed", 
         feedback: feedback.feedback || feedback.summary || "", 
         score: feedback.score ?? null,
         details: feedback
       };

       const nextReviewState = { ...reviewStateById, [taskId]: nextReview };
       let nextCompleted = completedTaskIds;
       if (passed) {
         nextCompleted = Array.from(new Set([...completedTaskIds, taskId]));
         markTaskCompleted();
        
         // Update XP locally if needed, though backend does it
         if (feedback.xpGained) {
           setUserContext(prev => ({
             ...prev,
             enrollment: {
               ...prev.enrollment,
               xp: (prev.enrollment.xp || 0) + feedback.xpGained
             }
           }));
         }
       }

       setCompletedTaskIds(nextCompleted);
       setReviewStateById(nextReviewState);
       writeProgress(userId, technology, { completedTaskIds: nextCompleted, reviewStateById: nextReviewState });
       setModalTab("feedback");
     } catch (e) {
       setServerError(String(e?.message || e));
     } finally {
       setSubmitting(false);
     }
   };
 const pollVideoStatus = async () => {
   if (!viewingTask) return;
   
   // If already ready, no need to poll
   if (videoStatus === "ready") return;
   
   // Set status to generating if it was error or idle
   if (videoStatus === "idle" || videoStatus === "error") {
     setVideoStatus("generating");
     setVideoMessage("Checking for video... please wait.");
   }
   
   const taskId = viewingTask.id || viewingTask.task_id;
   const videoUrl = `${API_BASE_URL}/videos/${userId}/${taskId}.mp4`;
   
   // Force a check
   startVideoPolling(videoUrl);
 };

 // Voice command handlers
 const onVoiceCommand = (command) => {
 const result = handleVoiceCommand(command);
 
 switch (result.type) {
 case 'open-mentor':
 setShowMentor(true);
 break;
 case 'open-video':
 openCurrentTask('video');
 break;
 case 'open-resources':
 openCurrentTask('resources');
 break;
 case 'open-submit':
 openCurrentTask('submit');
 break;
 case 'open-feedback':
 openCurrentTask('feedback');
 break;
 case 'open-visualize':
 openCurrentTask('visualize');
 break;
 case 'start-task':
 if (tasks.length > 0) {
 openCurrentTask('overview');
 }
 break;
 case 'next-task':
 if (viewIndex !== null && viewIndex < tasks.length - 1) {
 openTask(viewIndex + 1);
 }
 break;
 case 'previous-task':
 if (viewIndex !== null && viewIndex > 0) {
 openTask(viewIndex - 1);
 }
 break;
 case 'get-help':
 setShowMentor(true);
 break;
      default:
        // No action for unknown command types
        break;
      }
    };

 const onVoiceNavigate = (path) => {
 const result = handleVoiceNavigation(path);
 if (result.type === 'navigate') {
 navigate(result.path);
 }
 };


 const cacheBustUrl = (baseUrl) => `${baseUrl}${baseUrl.includes("?") ?"&" :"?"}v=${Date.now()}`;
 const stopVideoPolling = () => {
 if (videoPollRef.current) {
 clearInterval(videoPollRef.current);
 videoPollRef.current = null;
 }
 videoPollInFlightRef.current = false;
 videoPollStartedAtRef.current = null;
 };
 const startVideoPolling = (baseUrl, originProbeUrl) => {
 stopVideoPolling();
 videoBaseUrlRef.current = baseUrl;
 videoPollStartedAtRef.current = Date.now();

 const checkAvailability = async () => {
 const currentStatus = videoStatusRef.current || videoStatus;
 if (!videoBaseUrlRef.current || currentStatus ==="ready" || currentStatus ==="error") {
 return;
 }
 if (videoPollInFlightRef.current) return;
 videoPollInFlightRef.current = true;

 // Use backend endpoint to check if video exists (no CORS issues)
 const baseUrl = videoBaseUrlRef.current;
 if (!baseUrl) {
 videoPollInFlightRef.current = false;
 return;
 }
 
 try {
 // Check via backend endpoint - this avoids CORS issues
 const checkUrl = `${API_BASE_URL}/api/video/check-exists?url=${encodeURIComponent(baseUrl)}`;
 const res = await fetch(checkUrl, {
 method:"GET",
 cache:"no-store",
 });
 
 if (!res.ok) {
 videoPollInFlightRef.current = false;
 return;
 }
 
 const data = await res.json();
 
 if (data.ok && data.exists && data.status === 'ready') {
 // Video is ready!
 stopVideoPolling();
 const proxiedBaseUrl = getProxiedVideoUrl(baseUrl);
 const finalUrl = cacheBustUrl(proxiedBaseUrl);
 videoStatusRef.current ="ready";
 setVideoUrl(finalUrl);
 setVideoStatus("ready");
 if (data.usage) {
   setVideoUsage(data.usage);
 }
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 videoErrorRetriesRef.current = 0;
 console.log("[Video Polling] Video confirmed ready via backend:", finalUrl);
 return;
 } else if (data.ok === false || data.error || data.status === 'failed') {
 // Video generation failed or error occurred
 stopVideoPolling();
 videoStatusRef.current ="error";
 setVideoStatus("error");
 const errorMessage = data.error || data.message ||"Video generation failed. Please try regenerating.";
 setVideoError(errorMessage);
 setVideoMessage("");
 console.error("[Video Polling] Video generation failed:", {
 error: data.error,
 message: data.message,
 status: data.status
 });
 return;
 } else {
 // Video not ready yet, continue polling
 console.debug("[Video Polling] Video not ready yet, status:", data.status,"exists:", data.exists);
 }
 } catch (err) {
 // Log errors but don't stop polling immediately - video might still be processing
 console.warn("[Video Polling] Check failed:", err?.message || err);
 // If we've been polling for more than 3 minutes and getting errors, it might be a real issue
 const startedAt = videoPollStartedAtRef.current;
 if (startedAt && Date.now() - startedAt > 3 * 60 * 1000) {
 console.error("[Video Polling] Persistent errors after 3 minutes - video generation may have failed");
 }
 } finally {
 videoPollInFlightRef.current = false;
 }

 const startedAt = videoPollStartedAtRef.current;
 // Reduce timeout to 5 minutes since video generation should complete faster
 if (startedAt && Date.now() - startedAt > 5 * 60 * 1000) {
 stopVideoPolling();
 videoStatusRef.current ="error";
 setVideoStatus("error");
 setVideoError("Video generation timed out after 5 minutes. The video may have failed to generate. Please try regenerating.");
 setVideoMessage("");
 console.warn("[Video Polling] Timeout after 5 minutes");
 }
 };

 // Initial check immediately
 checkAvailability();
 // Poll every 15 seconds (increased from 12s for better reliability)
 videoPollRef.current = setInterval(() => {
 // Re-check timeout on each poll
 const startedAt = videoPollStartedAtRef.current;
 if (startedAt && Date.now() - startedAt > 10 * 60 * 1000) {
 stopVideoPolling();
 setVideoStatus("error");
 setVideoError("Video generation is taking longer than expected (10+ minutes). Please try regenerating.");
 return;
 }
 checkAvailability();
 }, 15000);
 };
 useEffect(() => {
 return () => {
 stopVideoPolling();
 };
 }, []);

 const handleVideoLoaded = () => {
 stopVideoPolling();
 setVideoStatus("ready");
 setVideoMessage("Your tutorial video is ready to watch!");
 setVideoError("");
 videoErrorRetriesRef.current = 0;
 };
 const handleVideoLoadError = () => {
 // Gracefully retry a few times as CloudFront propagates the object
 if (videoStatus ==="queued" || videoStatus ==="generating") return;
 const base = videoBaseUrlRef.current;
 if (base && videoErrorRetriesRef.current < 10) {
 videoErrorRetriesRef.current += 1;
 setVideoStatus("queued");
 setVideoMessage("Finalizing… We’ll auto-retry shortly.");
 setVideoError("");
 startVideoPolling(base);
 return;
 }
 setVideoStatus("error");
 setVideoError("Video failed to load. Please regenerate or try again later.");
 };

 const sendMentorMessage = async ({ text, imgFile }) => {
  if (!text && !imgFile) return;
  const you = { id: `u-${Date.now()}`, role: "user", text, imagePreview: imgFile ? URL.createObjectURL(imgFile) : null, when: new Date().toISOString() };
  const thinking = { id: `t-${Date.now()}`, role: "assistant", text: "Thinking…", typing: true, when: new Date().toISOString() };
  const add = (arr) => { setMentorMessages(arr); writeChat(userId, technology, arr); };

  const now = [...mentorMessages, you, thinking];
  add(now);
  const task = tasks[unlockedIndex];

  // Enhance text with technology context if it's a general question
  let enhancedText = text;
  if (text && text.length < 100 && !text.toLowerCase().includes(technology.toLowerCase())) {
    enhancedText = `Regarding ${technology}: ${text}`;
  }

  const res = await askMentorAPI({ userId, technology, experienceLevel, task, text: enhancedText, imgFile, history: mentorMessages });
  const final = [...now.filter((m) => !m.typing), { id: `a-${Date.now()}`, role: "assistant", text: res.answer || "I'm here to help.", when: new Date().toISOString() }];
  add(final);
};

const generateTopicExplanation = async (retryCount = 0, customContext = null) => {
  // If customContext is provided, use it. Otherwise, fallback to viewingTask.
  const activeTask = customContext?.task || viewingTask;
  
  if (!activeTask && !customContext) return;
  
  setExplanationLoading(true);
  setTopicExplanation(null);
  
  const languageName = ttsLanguages.find(l => l.code === explanationLanguage)?.name || 'English';
  
  const maxRetries = 2;
  const baseDelay = 1000; // 1 second base delay
  
  try {
    const data = await generateTopicExplanationAPI({
      userId, 
      technology: customContext?.technology || technology, 
      experienceLevel: customContext?.experienceLevel || experienceLevel,
      task: activeTask,
      language: explanationLanguage,
      explainType: customContext?.explainType,
      context: customContext?.context
    });
    
    // Check if response is successful
    if (!data.ok) {
       throw new Error(data.error || "Failed to generate topic explanation");
    }

    let explanationData = data.explanation?.explanation || data.explanation;
    
    if (typeof explanationData === 'string') {
      explanationData = { topicOverview: explanationData };
    }
    
    setTopicExplanation(explanationData);
    if (data.explanation?.usage) {
      setExplanationUsage(data.explanation.usage);
    } else if (data.usage) {
      setExplanationUsage(data.usage);
    }
  } catch (e) {
    console.error("Topic explanation error:", e);
    
    const isTimeoutError = e.message?.includes("timeout") || 
    e.message?.includes("504") ||
    e.message?.includes("taking longer");
    
    if (isTimeoutError && retryCount < maxRetries) {
      const delay = baseDelay * Math.pow(2, retryCount);
      console.log(`Retrying topic explanation after ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return generateTopicExplanation(retryCount + 1, customContext);
    }
    
    const errorMessage = e.message.includes("HTML instead of JSON")
    ?"Backend server is not running or endpoint not found. Please ensure the backend server is running on port 5000."
    : e.message ||"Failed to generate topic explanation. Please try again.";
    alert(errorMessage);
  } finally { setExplanationLoading(false); }
};

 const generateVideo = async () => {
 if (!viewingTask) return;
 
 // Simplified - no confirmation, just start
 setVideoGenerating(true);
 setVideoStatus("generating");
 setVideoMessage("Generating video... This may take a few minutes.");
 setVideoError("");
 stopVideoPolling();
 setVideoUrl(null);
 
 try {
 const data = await generateVideoAPI({
 userId,
 task: viewingTask,
 courseType:"technology",
 experienceLevel,
 language: selectedLanguage
 });
 
 const videoPath = data.videoUrl || data.expectedUrl;
 if (!videoPath) {
 throw new Error("Video generation service did not return a video URL.");
 }
 const absoluteUrl = videoPath.startsWith("http")
 ? videoPath
 : `${API_BASE_URL}${videoPath.startsWith("/") ? videoPath : `/${videoPath}`}`;

 // Use proxy URL for CloudFront videos to avoid CORS issues
 const proxiedAbsoluteUrl = getProxiedVideoUrl(absoluteUrl);
 const originProbeUrl = (data.originUrl && data.originUrl.startsWith("http"))
 ? getProxiedVideoUrl(data.originUrl)
 : null;

 if (data.queued) {
 setVideoStatus("queued");
 setVideoMessage("Rendering video… This usually takes 1-3 minutes. We'll refresh automatically.");
 // Use proxied URLs for polling
 startVideoPolling(proxiedAbsoluteUrl, originProbeUrl);
 setVideoUrl(proxiedAbsoluteUrl);
 setModalTab("video");
 alert(
 `🎬 Video rendering started for"${viewingTask.topic}".\n\n` +
 `Hang tight for ~2 minutes – the player will update automatically once the final MP4 is ready.`
 );
 return;
 }

 stopVideoPolling();
 const readyUrl = cacheBustUrl(proxiedAbsoluteUrl);
 setVideoUrl(readyUrl);
 setVideoStatus("ready");
 if (data.usage) {
   setVideoUsage(data.usage);
 }
 setVideoMessage("Your tutorial video is ready to watch!");
 setModalTab("video");
 
 // Simplified - no alert, just show in UI
 
 } catch (err) {
 console.error("Video generation failed:", err);
 stopVideoPolling();
 setVideoStatus("error");
 setVideoError(err.message ||"Failed to generate video.");
 
 // Provide more helpful error messages
 let errorMessage ="Failed to generate video. Please try again.";
 
 if (err.message.includes("HTML instead of JSON")) {
 errorMessage ="❌ Backend server is not running or Python dependencies not installed.\n\nPlease ensure:\n• Backend server is running on port 5000\n• Python dependencies are installed\n• All required packages are available";
 } else if (err.message.includes("timeout")) {
 errorMessage ="⏱️ Video generation is taking longer than expected.\n\nThis can happen with complex topics. Please try again.";
 } else if (err.message.includes("Missing required")) {
 errorMessage ="❌ Missing required information.\n\nPlease ensure the task has a valid topic and all required fields are filled.";
 } else if (err.message.includes("Video generation service")) {
 errorMessage ="🔧 Video generation service is temporarily unavailable.\n\nPlease try again in a few minutes.";
 } else if (err.message) {
 errorMessage = `❌ ${err.message}`;
 }
 
 alert(errorMessage);
 } finally {
 setVideoGenerating(false);
 }
 };

 const handleVisualize = async (retryCount = 0) => {
 if (!viewingTask) return;
 setVisualizing(true);
 setVisualUrl("");
 setVisualGeneratedAt(null);
 
 const maxRetries = 2;
 const baseDelay = 1000; // 1 second base delay
 
 try {
 const data = await visualizeOutputAPI({
 userId, technology, experienceLevel,
 task: viewingTask, hint: visualHint
 });
 // Backend returns visualUrl, ensure it's set correctly
 const imageUrl = data.visualUrl || data.imageUrl ||"";
 if (!imageUrl) {
 throw new Error("Visualization generation returned an empty URL. Please try again.");
 }
 setVisualUrl(imageUrl);
 setVisualGeneratedAt(new Date());
 setModalTab("visualize");
 console.log('✅ Visualization generated successfully');
 } catch (e) {
 console.error('❌ Visualization error:', e);
 // Retry on 504 Gateway Timeout errors with exponential backoff
 const isTimeoutError = e.message?.includes("timeout") || 
 e.message?.includes("504") ||
 e.message?.includes("taking longer");
 
 if (isTimeoutError && retryCount < maxRetries) {
 const delay = baseDelay * Math.pow(2, retryCount); // Exponential backoff: 1s, 2s
 console.log(`Retrying visualization after ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
 
 await new Promise(resolve => setTimeout(resolve, delay));
 return handleVisualize(retryCount + 1);
 }
 
 const errorMsg = e.message ||"Failed to generate visualization";
 alert(`❌ ${errorMsg}\n\nPlease try again. If the issue persists, check your internet connection and OpenAI API configuration.`);
 } finally { setVisualizing(false); }
 };


 /* ------------------------------ renders ------------------------------ */
 if (loading || userId === null) {
 return (
 <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center transition-colors duration-300">
 <div className="text-primary">Loading your bootcamp…</div>
 </div>
 );
 }

 if (notAllowed) {
 return (
 <div className="min-h-screen bg-white text-gray-900 flex items-center justify-center p-6 transition-colors duration-300">
 <div className="max-w-md w-full rounded-xl border border-secondary/40 bg-gray-50 p-5">
 <div className="text-primary font-semibold mb-2">Access restricted</div>
 <div className="text-sm text-gray-700">{notAllowed}</div>
 </div>
 </div>
 );
 }

 const go = (p) => navigate(p);
 const percentText = `${percent}%`;
 const explanation = { loading: explanationLoading, explanation: topicExplanation, onGenerate: generateTopicExplanation, language: explanationLanguage };

 return (
  <div className="technology-dashboard-wrapper">
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-black text-white" : "bg-bgMain text-body"}`}>
 {/* header */}
 <header className={`sticky top-0 z-10 border-b transition-colors duration-300 ${isDark ? "bg-black/90 border-accentGray/20 text-white" : "bg-bgHeaderFooter/90 border-secondary/30 text-gray-900"} backdrop-blur`}>
 <div className="mx-auto max-w-7xl px-4 py-4 flex items-center justify-between">
 <div>
 <h1 className={`text-xl md:text-2xl font-extrabold tracking-tight flex items-center gap-2 ${isDark ? "text-primary" : "text-primary"}`}>
 <ClipboardDocumentListIcon className="h-6 w-6 text-primary" /> {technology ? `${technology} Bootcamp` :"Bootcamp"}
 </h1>
 <p className={`text-sm flex items-center gap-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
 <span>Hi {userName}</span> <span>👋</span> <span>Welcome back.</span>
 </p>
 <p className={`mt-1 text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
 <span className="mr-3">User ID: <span className="text-primary">{userId ||"-"}</span></span>
 <span className="mr-3">Track: <span className="text-primary capitalize">{track ||"-"}</span></span>
 <span>Experience: <span className="text-primary">{experienceLevel ||"-"}</span></span>
 </p>
 <div className="mt-2">
 <SubscriptionStatus />
 </div>
 </div>
 <div className="hidden sm:flex items-center gap-3">
 <QuickLink label="Voice Assistant" icon={<SpeakerWaveIcon className="h-4 w-4" />} onClick={openVoiceAssistant} />
 <QuickLink label="Certificate" icon={<TrophyIcon className="h-4 w-4" />} onClick={() => navigate(`/bootcamp/certificate`)} />
 <QuickLink 
 label={llmTrialStatus?.hasActiveTrial ? `AI Mentor (${llmTrialStatus?.daysRemaining}d left)` :"AI Mentor"} 
 icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />} 
 onClick={() => {
 if (llmTrialStatus?.hasActiveTrial) {
 setShowMentor(true);
 } else if (llmTrialStatus && llmTrialStatus.daysRemaining <= 0) {
 setShowTrialExpiredModal(true);
 } else {
 setShowTrialModal(true);
 }
 }}
 />
 </div>
 </div>
 </header>

  {/* main */}
  <main className="mx-auto max-w-7xl px-4 py-6 space-y-6">
    {/* Voice Assistant & Quick Actions */}
    <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
      <ActionCard
        title="Voice Assistant"
        subtitle="Interactive AI-powered voice guidance for your tasks"
        icon={<SpeakerWaveIcon className="h-6 w-6 text-primary" />}
        onClick={openVoiceAssistant}
      />
      <ActionCard
        title="Certificate"
        subtitle="View requirements or download your earned certificate"
        icon={<TrophyIcon className="h-6 w-6 text-primary" />}
        onClick={() => navigate('/bootcamp/certificate')}
      />
      <ActionCard
        title="AI Mentor"
        subtitle="Get personalized help and guidance from our AI expert"
        icon={<ChatBubbleLeftRightIcon className="h-6 w-6 text-primary" />}
        onClick={() => {
          if (llmTrialStatus?.hasActiveTrial) {
            setShowMentor(true);
          } else if (llmTrialStatus && llmTrialStatus.daysRemaining <= 0) {
            setShowTrialExpiredModal(true);
          } else {
            setShowTrialModal(true);
          }
        }}
      />
    </section>

    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Sidebar: Task List */}
      <aside className="lg:col-span-1 space-y-4">
        <div className={`p-4 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/40 shadow-sm"}`}>
          <h3 className={`font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>Course Roadmap</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
            {tasks.map((t, idx) => {
              const isLocked = idx > unlockedIndex;
              const isCompleted = completedTaskIds.includes(t.id);
              const isActive = viewIndex === idx;
              
              return (
                <button
                  key={t.id}
                  disabled={isLocked}
                  onClick={() => setViewIndex(idx)}
                  className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${
                    isActive 
                      ? "border-primary bg-primary/10" 
                      : isLocked 
                        ? "opacity-50 cursor-not-allowed border-transparent" 
                        : isDark ? "border-accentGray/10 hover:border-accentGray/30" : "border-gray-100 hover:border-primary/30"
                  }`}
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                    isCompleted ? "bg-green-500/10 text-green-500" : isActive ? "bg-primary text-white" : "bg-secondary/10 text-muted"
                  }`}>
                    {isCompleted ? <CheckCircleIconSolid className="h-5 w-5" /> : <span className="text-sm font-bold">{idx + 1}</span>}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-bold truncate ${isActive ? "text-primary" : isDark ? "text-white" : "text-gray-900"}`}>{t.title || t.topic}</p>
                    <p className="text-xs text-muted truncate">{isLocked ? "Locked" : isCompleted ? "Completed" : "In Progress"}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Main Content: 4 Sections */}
      <div className="lg:col-span-3 space-y-6">
        <div className={`p-6 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/40 shadow-sm"}`}>
          {/* Header & Tabs */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted font-bold">Task {viewIndex + 1} of {tasks.length || 100}</p>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{tasks[viewIndex]?.title || tasks[viewIndex]?.topic}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
                                   <TabButton active={modalTab === "overview"} onClick={() => setModalTab("overview")}>Overview</TabButton>
                                   <TabButton active={modalTab === "video"} onClick={() => setModalTab("video")}>Video</TabButton>
                                   <TabButton active={modalTab === "submit"} onClick={() => setModalTab("submit")}>Verify My Work</TabButton>
                                   {githubStatus && invitationStatus === 'accepted' && <TabButton active={modalTab === "github"} onClick={() => setModalTab("github")}>GitHub Status</TabButton>}
                                   <TabButton active={modalTab === "feedback"} onClick={() => setModalTab("feedback")}>Feedback</TabButton>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {tasks[viewIndex] ? (
              <>
                {modalTab === "overview" && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <Field label="Description">
                        <p className={`text-sm leading-relaxed ${isDark ? "text-gray-300" : "text-gray-800"}`}>{tasks[viewIndex].description}</p>
                      </Field>
                      {tasks[viewIndex].useCase && (
                        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
                          <h5 className="text-xs font-bold text-primary uppercase tracking-wider flex items-center gap-2">
                            <DocumentTextIcon className="h-4 w-4" /> Use Case
                          </h5>
                          <p className={`text-sm leading-relaxed font-medium ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {tasks[viewIndex].useCase}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-4">
                      {tasks[viewIndex].expectedOutput && (
                        <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4 space-y-2">
                          <h5 className="text-xs font-bold text-green-600 uppercase tracking-wider flex items-center gap-2">
                            <CheckCircleIcon className="h-4 w-4" /> Expected Output
                          </h5>
                          <p className={`text-sm leading-relaxed font-medium ${isDark ? "text-gray-100" : "text-gray-900"}`}>
                            {tasks[viewIndex].expectedOutput}
                          </p>
                        </div>
                      )}
                        {tasks[viewIndex].subtopics?.length > 0 && (
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-gray-50"}`}>
                            <p className="text-sm font-bold mb-2">Key Topics:</p>
                            <ul className="list-disc pl-5 space-y-1 text-sm text-muted">
                              {tasks[viewIndex].subtopics.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {modalTab === "video" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {videoUrl ? (
                      <div className="space-y-6">
                        <div className={`aspect-video rounded-2xl overflow-hidden border ${isDark ? "bg-black border-accentGray/20" : "bg-gray-100 border-gray-200 shadow-inner"}`}>
                          <video 
                            src={videoUrl} 
                            controls 
                            className="w-full h-full object-contain bg-black"
                            poster={tasks[viewIndex]?.thumbnail || ""}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 mr-4">
                            <h4 className="font-bold truncate">Tutorial: {tasks[viewIndex]?.title}</h4>
                            <p className="text-sm text-muted">Guided video instruction for this task.</p>
                          </div>
                          <Button variant="outlined" size="small" onClick={() => openCurrentTask("video")}>Refresh Video</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="p-4 bg-primary/10 rounded-full mb-4">
                          <PlayIcon className="h-10 w-10 text-primary" />
                        </div>
                        <h4 className={`text-lg font-bold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}>Video Not Ready</h4>
                        <p className="text-sm text-muted max-w-md mb-6">Our AI is preparing your video tutorial. This might take a minute.</p>
                        <Button variant="contained" onClick={pollVideoStatus}>Check Status</Button>
                      </div>
                    )}
                  </div>
                )}

                {modalTab === "submit" && (
                  <TaskModal
                    task={tasks[viewIndex]}
                    resources={resourcesByTaskId[tasks[viewIndex].id]}
                    onClose={() => {}} // No-op for main panel
                    tab="submit"
                    isInline={true}
                    reviewState={reviewStateById[tasks[viewIndex].id]}
                    onSubmit={handleVerifyMyWork}
                    explanation={explanation}
                    explanationLanguage={explanationLanguage}
                    setExplanationLanguage={setExplanationLanguage}
                    githubRepoUrl={githubRepoUrl}
                    invitationStatus={invitationStatus}
                    githubStatus={githubStatus}
                    syncInvitationStatus={syncInvitationStatus}
                    githubUrl={githubUrl}
                    setGithubUrl={setGithubUrl}
                    submissionType={submissionType}
                    setSubmissionType={setSubmissionType}
                    explainWithAI={explainWithAI}
                    experienceLevel={experienceLevel}
                    userId={userId}
                  />
                )}
                
                {modalTab === "github" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                    <div className={`p-6 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-gray-50 border-gray-200"}`}>
                      <h4 className="font-bold mb-4 flex items-center gap-2">
                        <GitHubIcon className="h-5 w-5" /> GitHub Repository
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <p className="text-sm font-bold text-muted">Repository Details</p>
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-white border"}`}>
                            <p className="text-xs text-muted mb-1">Repository Name</p>
                            <p className="font-mono text-sm">{githubStatus.repo_name}</p>
                            <a href={githubStatus.repo_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary font-bold mt-2 inline-block">View on GitHub ↗</a>
                          </div>
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-white border"}`}>
                            <p className="text-xs text-muted mb-1">Invitation Status</p>
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              githubStatus.invite_status === 'accepted' ? 'bg-green-500/10 text-green-500' : 
                              githubStatus.invite_status === 'pending' ? 'bg-amber-500/10 text-amber-500' : 'bg-gray-500/10 text-gray-500'
                            }`}>
                              {githubStatus.invite_status?.toUpperCase()}
                            </span>
                            {githubStatus.invite_status === 'pending' && (
                              <p className="text-xs text-amber-500 mt-2">Please accept your GitHub invitation to continue project-based tasks.</p>
                            )}
                          </div>
                        </div>
                        <div className="space-y-4">
                          <p className="text-sm font-bold text-muted">Sync Status</p>
                          <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-white border"}`}>
                            <p className="text-xs text-muted mb-1">Last Synced</p>
                            <p className="text-sm">{githubStatus.updated_at ? new Date(githubStatus.updated_at).toLocaleString() : "—"}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {modalTab === "feedback" && (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 text-center py-12">
                    <ChatBubbleLeftRightIcon className="h-12 w-12 text-primary/20 mx-auto mb-4" />
                    <p className="text-muted">No feedback available yet. Submit your task first.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="mt-4 text-muted font-medium">Preparing your workspace...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </main>
</div>

 {/* Task modal */}
 {showModal && viewingTask && (
 <TaskModal
 key={`${viewIndex}-${viewingTask.id}`}
 task={viewingTask}
 resources={resourcesByTaskId[viewingTask.id] || []}
 onClose={() => setShowModal(false)}
 tab={modalTab}
 setTab={setModalTab}
 reviewState={reviewStateById[viewingTask.id]}
 language={selectedLanguage}
 setLanguage={() => {}} // Language selection removed - always English
 githubRepoUrl={githubRepoUrl}
 invitationStatus={invitationStatus}
 githubUrl={githubUrl}
 setGithubUrl={setGithubUrl}
 submissionType={submissionType}
 setSubmissionType={setSubmissionType}
 explanationUsage={explanationUsage}
 videoUsage={videoUsage}
 explainWithAI={explainWithAI}
 experienceLevel={experienceLevel}
 userId={userId}
 onSubmit={handleVerifyMyWork}
 explanation={{
 loading: explanationLoading, explanation: topicExplanation, onGenerate: generateTopicExplanation, language: explanationLanguage
 }}
 explanationLanguage={explanationLanguage}
 setExplanationLanguage={setExplanationLanguage}
 video={{
 generating: videoGenerating,
 url: videoUrl,
 onGenerate: generateVideo,
 status: videoStatus,
 message: videoMessage,
 error: videoError,
 onLoaded: handleVideoLoaded,
 onError: handleVideoLoadError
 }}
 visualize={{
 show: true,
 visualizing,
 visualUrl,
 visualHint,
 visualGeneratedAt,
 setVisualHint,
            onVisualize: handleVisualize
          }}
          githubStatus={githubStatus}
          syncInvitationStatus={syncInvitationStatus}
        />
 )}

 {/* 🎯 LLM Trial Active Modal */}
 {showTrialModal && llmTrialStatus?.hasActiveTrial && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-bgMain/70 p-4">
 <div className="w-full max-w-md bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 border border-blue-400/50 rounded-2xl shadow-2xl p-8 text-center">
 <div className="text-5xl mb-4">🎉</div>
 <h2 className="text-2xl font-bold text-blue-100 mb-4">Welcome to AI Mentor!</h2>
 <p className="text-blue-200 mb-6 text-sm leading-relaxed">
 You have a <strong className="text-primary">7-day FREE trial</strong> to access our premium AI Mentor feature. Get personalized guidance, ask questions, and accelerate your learning!
 </p>
 <div className="bg-blue-800/50 border border-blue-400/30 rounded-lg p-4 mb-6">
 <p className="text-blue-100 font-semibold">
 Trial expires in <span className="text-primary text-lg">{llmTrialStatus?.daysRemaining}</span> days
 </p>
 </div>
        <button
          onClick={() => {
            setShowTrialModal(false);
            setShowMentor(true);
          }}
          className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold py-3 rounded-lg hover:shadow-lg transition mb-3"
        >
          Start Your Free Trial →
        </button>
 <button
 onClick={() => setShowTrialModal(false)}
 className="w-full bg-blue-700/50 border border-blue-400/50 text-blue-100 font-medium py-2 rounded-lg hover:bg-blue-700 transition"
 >
 Maybe Later
 </button>
 </div>
 </div>
 )}

 {/* 🔒 LLM Trial Expired Modal */}
 {showTrialExpiredModal && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-bgMain/70 p-4">
 <div className="w-full max-w-md bg-gradient-to-br from-red-900 via-orange-900 to-red-900 border border-red-400/50 rounded-2xl shadow-2xl p-8 text-center">
 <div className="text-5xl mb-4">⏰</div>
 <h2 className="text-2xl font-bold text-red-100 mb-4">Trial Period Expired</h2>
 <p className="text-red-200 mb-6 text-sm leading-relaxed">
 Your <strong className="text-primary">7-day free trial</strong> for AI Mentor has ended. Upgrade to <strong className="text-primary">Premium</strong> to continue accessing advanced features!
 </p>
 <div className="bg-red-800/50 border border-red-400/30 rounded-lg p-4 mb-6">
 <ul className="text-red-100 text-sm space-y-2 text-left">
 <li>✨ Unlimited AI Mentor Access</li>
 <li>🎯 Advanced Challenge Levels</li>
 <li>📊 Personalized Learning Paths</li>
 <li>🏆 Certificate Programs</li>
 </ul>
 </div>
        <button
          onClick={() => {
            setShowTrialExpiredModal(false);
            navigate('/subscription-plans');
          }}
          className="w-full bg-gradient-to-r from-primary to-secondary text-white font-bold py-3 rounded-lg hover:shadow-lg transition mb-3"
        >
          Upgrade to Premium Now
        </button>
 <button
 onClick={() => setShowTrialExpiredModal(false)}
 className="w-full bg-red-700/50 border border-red-400/50 text-red-100 font-medium py-2 rounded-lg hover:bg-red-700 transition"
 >
 Close
 </button>
 </div>
 </div>
 )}

 {/* Mentor modal */}
 {showMentor && (
 <MentorModal
 onClose={() => setShowMentor(false)}
 technology={technology}
 task={tasks[viewIndex]}
 explanation={topicExplanation}
 />
 )}

 {/* Voice Assistant */}
 <button 
   onClick={() => openVoiceAssistant()}
   className="fixed bottom-6 right-6 z-50 p-4 bg-primary text-white rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all group"
   title="Open Voice Assistant"
 >
   <SpeakerWaveIcon className="h-6 w-6" />
   <span className="absolute right-full mr-3 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
     Voice Assistant
   </span>
 </button>

 <VoiceAssistant
   isOpen={isVoiceAssistantOpen}
   onClose={closeVoiceAssistant}
 initialMessage={vaInitialMessage}
 onCommand={handleVoiceCommand}
 onNavigate={handleVoiceNavigation}
 currentTask={viewingTask}
 userContext={{
 userId,
 technology,
 experienceLevel,
 currentTaskIndex: viewIndex,
 totalTasks: (tasks || []).length
 }}
 />
 </div>
 );
}

/* --------------------------------- UI bits -------------------------------- */
function QuickLink({ label, icon, onClick }) {
 return (
 <button onClick={onClick} className="flex items-center gap-1 rounded-xl border border-secondary/50 text-primary hover:text-primary hover:border-primary transition px-4 py-2 text-sm">
 {icon} {label}
 </button>
 );
}
function ActionCard({ title, subtitle, icon, onClick }) {
 return (
 <button onClick={onClick} className="flex flex-col items-start gap-3 rounded-xl border border-secondary/30 bg-bgMain/40 p-5 text-left hover:border-primary hover:bg-secondary/5 transition-all duration-200 group">
 <div className="flex items-center gap-3">
 <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition">
 {icon}
 </div>
 <h4 className="font-semibold text-sm group-hover:text-primary transition">{title}</h4>
 </div>
 <p className="text-xs text-muted group-hover:text-muted transition leading-relaxed">
 {subtitle.length > 50 ? `${subtitle.substring(0, 50)}...` : subtitle}
 </p>
 </button>
 );
}
function ProgressRing({ percent = 0, size = 200, stroke = 16 }) {
 const radius = (size - stroke) / 2, c = 2 * Math.PI * radius, dash = ((100 - percent) / 100) * c;
 return (
 <div className="flex items-center gap-4">
 <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
 <circle cx={size/2} cy={size/2} r={radius} stroke="#3f3f46" strokeWidth={stroke} fill="none"/>
 <circle cx={size/2} cy={size/2} r={radius} stroke="#fde047" strokeWidth={stroke} fill="none" strokeDasharray={c} strokeDashoffset={dash} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}/>
 </svg>
 <div><div className="text-4xl font-extrabold text-primary">{percent}%</div><div className="text-sm text-muted">Overall progress</div></div>
 </div>
 );
}
function LinearProgress({ percent = 0 }) {
 return (
 <div className="mt-3">
 <div className="h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
 <div className="h-2 bg-primary transition-all" style={{ width: `${clamp(percent, 0, 100)}%` }} />
 </div>
 <div className="mt-1 text-[11px] text-muted">{percent}% complete</div>
 </div>
 );
}

function TaskModal({ task, resources = [], onClose, tab, setTab, reviewState, onSubmit, explanation, video, visualize, language, setLanguage, explanationLanguage, setExplanationLanguage, githubRepoUrl, invitationStatus, githubUrl, setGithubUrl, submissionType, setSubmissionType, explanationUsage, videoUsage, githubStatus, syncInvitationStatus, explainWithAI, experienceLevel, userId, isInline = false }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [docFile, setDocFile] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [comments, setComments] = useState("");
  const [sqlScript, setSqlScript] = useState("");
  const [queryOutput, setQueryOutput] = useState("");
  const [commands, setCommands] = useState("");
  const [terminalOutput, setTerminalOutput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

 // Auto-detect evidence type based on task
 const [evidenceInfo, setEvidenceInfo] = useState(null);

 // TTS (Text-to-Speech) state — declared before any useEffect
 const [ttsLanguage, setTtsLanguage] = useState('en');
 const [isSpeaking, setIsSpeaking] = useState(false);
 const [speechSynthesis, setSpeechSynthesis] = useState(null);
 const speechUtteranceRef = useRef(null);

 // Auto-detect evidence requirements when task changes
 useEffect(() => {
   if (task) {
     const evidence = determineEvidenceType(task.topic, task.task_title, task);
     setEvidenceInfo(evidence);

     // Auto-set submission type if GitHub is required
     if (evidence.requiresGithub && githubRepoUrl) {
       setSubmissionType("github");
     } else if ((evidence.methods.includes("files") || evidence.requiresScreenshot) && !evidence.requiresGithub) {
       setSubmissionType("files");
     }
   }
 }, [task, githubRepoUrl, setSubmissionType]);

 // Initialize speech synthesis
 useEffect(() => {
 if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
 setSpeechSynthesis(window.speechSynthesis);
 }
 }, []);

 // Language options for TTS
 const ttsLanguages = [
 { code: 'en', name: 'English', voice: 'en-US' },
 // Indian Languages
 { code: 'hi', name: 'Hindi (हिंदी)', voice: 'hi-IN' },
 { code: 'ta', name: 'Tamil (தமிழ்)', voice: 'ta-IN' },
 { code: 'te', name: 'Telugu (తెలుగు)', voice: 'te-IN' },
 { code: 'kn', name: 'Kannada (ಕನ್ನಡ)', voice: 'kn-IN' },
 { code: 'ml', name: 'Malayalam (മലയാളം)', voice: 'ml-IN' },
 { code: 'bn', name: 'Bengali (বাংলা)', voice: 'bn-IN' },
 { code: 'gu', name: 'Gujarati (ગુજરાતી)', voice: 'gu-IN' },
 { code: 'mr', name: 'Marathi (मराठी)', voice: 'mr-IN' },
 { code: 'pa', name: 'Punjabi (ਪੰਜਾਬੀ)', voice: 'pa-IN' },
 { code: 'ur', name: 'Urdu (اردو)', voice: 'ur-IN' },
 { code: 'or', name: 'Odia (ଓଡ଼ିଆ)', voice: 'or-IN' },
 { code: 'as', name: 'Assamese (অসমীয়া)', voice: 'as-IN' },
 // Other Languages
 { code: 'es', name: 'Spanish', voice: 'es-ES' },
 { code: 'fr', name: 'French', voice: 'fr-FR' },
 { code: 'de', name: 'German', voice: 'de-DE' },
 { code: 'it', name: 'Italian', voice: 'it-IT' },
 { code: 'pt', name: 'Portuguese', voice: 'pt-BR' },
 { code: 'ja', name: 'Japanese', voice: 'ja-JP' },
 { code: 'ko', name: 'Korean', voice: 'ko-KR' },
 { code: 'zh', name: 'Chinese', voice: 'zh-CN' },
 { code: 'ar', name: 'Arabic', voice: 'ar-SA' },
 { code: 'ru', name: 'Russian', voice: 'ru-RU' },
 ];

 // Stop speaking
 const stopSpeaking = () => {
 if (speechSynthesis && speechSynthesis.speaking) {
 speechSynthesis.cancel();
 setIsSpeaking(false);
 }
 };

 // Speak text in chunks for better flow and sync
 const speakText = (text, lang = ttsLanguage) => {
   if (!speechSynthesis || !text) return;

   // Stop any ongoing speech
   stopSpeaking();

   // Clean text (remove markdown, code blocks, etc.)
   const cleanText = text
     .replace(/```[\s\S]*?```/g, '') // Remove code blocks
     .replace(/`[^`]+`/g, '') // Remove inline code
     .replace(/#{1,6}\s+/g, '') // Remove markdown headers
     .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
     .replace(/\*([^*]+)\*/g, '$1') // Remove italic
     .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links
     .trim();

   if (!cleanText) return;

   // Split into sentences for better natural flow
   const sentences = cleanText.split(/[.!?]+\s+/).filter(s => s.trim().length > 0);
   if (sentences.length === 0) return;

   setIsSpeaking(true);
   let currentSentenceIndex = 0;

   const speakNextSentence = () => {
     if (currentSentenceIndex >= sentences.length) {
       setIsSpeaking(false);
       return;
     }

     const utterance = new SpeechSynthesisUtterance(sentences[currentSentenceIndex]);
     const selectedLang = ttsLanguages.find(l => l.code === lang) || ttsLanguages[0];
    
     utterance.lang = selectedLang.voice;
     utterance.rate = 0.95; // Slightly faster for natural flow
     utterance.pitch = 1;
     utterance.volume = 1;

     // Find voice
     const voices = speechSynthesis.getVoices();
     const voice = voices.find(v => v.lang.startsWith(selectedLang.code)) || 
                   voices.find(v => v.lang.includes(selectedLang.code)) ||
                   voices[0];
    
     if (voice) utterance.voice = voice;

     utterance.onend = () => {
       currentSentenceIndex++;
       // Small pause between sentences (250ms)
       setTimeout(speakNextSentence, 250);
     };

     utterance.onerror = (error) => {
       if (error.error !== 'interrupted') {
         console.error('Speech synthesis error:', error);
       }
       setIsSpeaking(false);
     };

     speechUtteranceRef.current = utterance;
     speechSynthesis.speak(utterance);
   };

   speakNextSentence();
 };

 // Speak full explanation
 const speakFullExplanation = () => {
 if (!explanation?.explanation) return;

 const fullText = [
 safeToString(explanation.explanation.topicOverview),
 Array.isArray(explanation.explanation.keyConcepts) ? explanation.explanation.keyConcepts.map(safeToString).join('. ') : safeToString(explanation.explanation.keyConcepts),
 Object.values(explanation.explanation.subtopicExplanations || {}).map(safeToString).join('. '),
 safeToString(explanation.explanation.practicalExamples),
 safeToString(explanation.explanation.commonMistakes),
 safeToString(explanation.explanation.bestPractices)
 ].filter(Boolean).join('. ');

 speakText(fullText);
 };

 // Cleanup on unmount
 useEffect(() => {
 return () => {
 if (speechSynthesis && speechSynthesis.speaking) {
 speechSynthesis.cancel();
 }
 };
 }, [speechSynthesis]);

  if (isInline) {
    return (
      <div className={`w-full ${isDark ? "bg-bgSection" : "bg-white"} space-y-4`}>
        <div className="max-h-[800px] overflow-y-visible space-y-4">
          {tab === "submit" && (
            <div className="space-y-6">
              {/* Submission Logic - Extracted from the modal's "submit" tab */}
              {evidenceInfo && (
                <div className={`p-4 rounded-xl border ${isDark ? "bg-primary/5 border-primary/20" : "bg-primary/5 border-primary/10"}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-primary/20 rounded-lg">
                      <ClipboardDocumentCheckIcon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-bold text-primary">AI Mentor Guidance</h4>
                      <p className="text-xs text-muted">Based on the expected output, I've prepared the following evidence fields for you to complete.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className={`p-4 rounded-xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/20"}`}>
                  <h5 className="text-sm font-bold mb-3 flex items-center gap-2">
                    <GitHubIcon className="h-4 w-4" /> GitHub Repository
                  </h5>
                  
                  {!githubRepoUrl ? (
                    <div className="space-y-3">
                      <p className="text-xs text-muted italic">No repository provisioned. Please contact support if you enrolled with a GitHub username.</p>
                      <div className="flex flex-col gap-2">
                        <p className="text-[10px] font-bold uppercase text-muted">OR: Provide your own repository URL</p>
                        <input 
                          type="text" 
                          placeholder="https://github.com/..." 
                          className={`w-full px-3 py-2 text-sm rounded-lg border ${isDark ? "bg-bgMain border-accentGray/20 text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                          value={githubUrl}
                          onChange={(e) => setGithubUrl(e.target.value)}
                        />
                      </div>
                      <button 
                        onClick={syncInvitationStatus}
                        className="text-xs font-bold text-primary flex items-center gap-1 hover:underline"
                      >
                        <ArrowPathIcon className="h-3 w-3" /> Check Status
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono truncate max-w-[150px]">{githubRepoUrl}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          invitationStatus === 'ACCEPTED' ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'
                        }`}>
                          {invitationStatus || 'PENDING'}
                        </span>
                      </div>
                      <a href={githubRepoUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary font-bold hover:underline">View Repo ↗</a>
                    </div>
                  )}
                </div>

                <div className={`p-4 rounded-xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-white border-secondary/20"}`}>
                   <div className="grid grid-cols-2 gap-4">
                     <div>
                       <p className="text-[10px] font-bold text-muted uppercase mb-1">Screenshot/Image</p>
                       <input 
                         type="file" 
                         className="hidden" 
                         id="img-upload-inline" 
                         accept="image/*"
                         onChange={(e) => setImgFile(e.target.files[0])}
                       />
                       <label 
                         htmlFor="img-upload-inline"
                         className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                           imgFile ? "border-primary bg-primary/5 text-primary" : "border-secondary/20 hover:border-primary/50"
                         }`}
                       >
                         <div className="text-center">
                           <p className="text-[11px] font-bold truncate max-w-[80px]">{imgFile ? imgFile.name : "Choose File"}</p>
                         </div>
                       </label>
                     </div>
                     <div>
                       <p className="text-[10px] font-bold text-muted uppercase mb-1">Documentation (PDF/MD)</p>
                       <input 
                         type="file" 
                         className="hidden" 
                         id="doc-upload-inline" 
                         accept=".pdf,.md,.docx,.txt"
                         onChange={(e) => setDocFile(e.target.files[0])}
                       />
                       <label 
                         htmlFor="doc-upload-inline"
                         className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                           docFile ? "border-primary bg-primary/5 text-primary" : "border-secondary/20 hover:border-primary/50"
                         }`}
                       >
                         <div className="text-center">
                           <p className="text-[11px] font-bold truncate max-w-[80px]">{docFile ? docFile.name : "Choose File"}</p>
                         </div>
                       </label>
                     </div>
                   </div>
                </div>
              </div>

              <div className={`p-4 rounded-xl border ${isDark ? "bg-black border-accentGray/20" : "bg-zinc-900 border-zinc-800"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CommandLineIcon className="h-4 w-4 text-green-500" />
                  <p className="text-[10px] font-bold text-green-500 uppercase">Terminal Output</p>
                </div>
                <textarea 
                  placeholder="Paste your terminal logs here..."
                  className="w-full h-24 bg-transparent text-green-500 font-mono text-xs focus:outline-none resize-none"
                  value={terminalOutput}
                  onChange={(e) => setTerminalOutput(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <p className="text-xs font-bold text-muted uppercase">Additional Comments</p>
                <textarea 
                  placeholder="Tell your AI mentor about your implementation details..."
                  className={`w-full h-20 px-3 py-2 text-sm rounded-xl border ${isDark ? "bg-bgMain border-accentGray/20 text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </div>

              {serverError && <p className="text-xs text-red-500 font-medium italic">{serverError}</p>}

              <button 
                disabled={submitting || (!docFile && !imgFile && !githubUrl && !terminalOutput)}
                onClick={async () => {
                  setSubmitting(true);
                  setServerError("");
                  try {
                    await onSubmit({ docFile, imgFile, comments, githubUrl, terminalOutput, setSubmitting, setServerError });
                  } catch (e) {
                    setServerError(e.message);
                  } finally {
                    setSubmitting(false);
                  }
                }}
                className={`w-full py-4 rounded-2xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 ${
                  submitting ? "bg-gray-400 cursor-not-allowed" : "bg-primary text-white hover:scale-[1.02] active:scale-[0.98]"
                }`}
              >
                {submitting ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Analyzing Submission...</span>
                  </>
                ) : (
                  <>
                    <RocketLaunchIcon className="h-5 w-5" />
                    <span>Submit for AI Review</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-4xl bg-[#f0f9ff] border border-sky-200 rounded-2xl shadow-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <ClipboardDocumentListIcon className="h-6 w-6 text-primary" />
            </div>
            <h3 className="font-bold text-xl text-zinc-900">Task Details</h3>
          </div>
          <button className="p-2 rounded-full hover:bg-zinc-100 transition-colors" onClick={onClose}>
            <XMarkIcon className="h-6 w-6 text-zinc-400" />
          </button>
        </div>

 <div className="px-5 pt-3">
 <div className="flex gap-2">
                          <TabButton active={tab==="overview"} onClick={()=>setTab("overview")}>Overview</TabButton>
                          <TabButton active={tab==="resources"} onClick={()=>setTab("resources")}>AI Mentor & Verification</TabButton>
                          <TabButton active={tab==="video"} onClick={()=>setTab("video")}>Video</TabButton>
                           {githubStatus && invitationStatus === 'accepted' && <TabButton active={tab==="github"} onClick={()=>setTab("github")}>GitHub Status</TabButton>}
                           <TabButton active={tab==="feedback"} onClick={()=>setTab("feedback")}>Feedback</TabButton>
            </div>
 </div>

 <div className="px-5 py-4 max-h-[70vh] overflow-y-auto space-y-4">
 {tab ==="overview" && (
 <div className="space-y-4 text-left">
 <Field label="Topic"><div className="text-base font-semibold text-gray-900">{task.title || task.topic}</div></Field>
 {task.description && <Field label="Description"><p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{task.description}</p></Field>}
 {task.useCase && <Info label="Use case" variant="primary">{task.useCase}</Info>}
 {task.futureUseCase && <Info label="Future use case">{task.futureUseCase}</Info>}
 {task.expectedOutput && <Info label="Expected output" variant="success">{task.expectedOutput}</Info>}
 </div>
 )}

 {(tab === "resources" || tab === "submit" || tab === "feedback") && (
   <div className="bg-white rounded-xl min-h-[500px]">
     <AIMentorController 
       task={task}
       enrollmentId={localStorage.getItem('enrollment_id')}
       experienceLevel={experienceLevel || 'Beginner'}
       userId={userId}
       onComplete={() => {
         // Automatically refresh dashboard state when task is completed via AI Mentor
         window.location.reload();
       }}
     />
   </div>
 )}

 {tab ==="resources" && (
 <div className="space-y-6">
 {/* AI Generated Explanation */}
 {explanation?.explanation && (
 <div className="space-y-4">
 {/* TTS Controls */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-4">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <SpeakerWaveIcon className="h-5 w-5" />
 Listen to Explanation
 </h4>
 <div className="flex items-center gap-2">
 <select
 value={explanationLanguage}
 onChange={(e) => {
   setExplanationLanguage(e.target.value);
   if (explanation?.explanation) {
     setTimeout(() => explanation.onGenerate(), 100);
   }
 }}
 className="bg-bgMain/60 border border-secondary/30 rounded-lg px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-primary"
 disabled={isSpeaking}
 >
 {ttsLanguages.map((lang) => (
 <option key={lang.code} value={lang.code}>
 {lang.name}
 </option>
 ))}
 </select>
 {isSpeaking ? (
 <button
 onClick={stopSpeaking}
 className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2"
 >
 <XMarkIcon className="h-4 w-4" />
 Stop
 </button>
 ) : (
 <button
 onClick={speakFullExplanation}
 className="bg-primary hover:bg-primary text-white px-4 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2"
 >
 <SpeakerWaveIcon className="h-4 w-4" />
 Listen All
 </button>
 )}
 </div>
 </div>
 <p className="text-xs text-muted">
 Select your preferred language and click"Listen All" to hear the complete explanation, or use the play buttons next to each section.
 </p>
 </div>

 {/* Topic Overview */}
 {explanation.explanation.topicOverview && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <BookOpenIcon className="h-5 w-5" />
 Topic Overview
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanation.explanation.topicOverview))}
 className="text-primary hover:text-primary p-1.5 rounded hover:bg-primary/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.topicOverview)}
 </p>
 </div>
 )}

 {/* Key Concepts */}
 {explanation.explanation.keyConcepts && explanation.explanation.keyConcepts.length > 0 && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <ClipboardDocumentListIcon className="h-5 w-5" />
 Key Concepts
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(Array.isArray(explanation.explanation.keyConcepts) ? explanation.explanation.keyConcepts.map(safeToString).join('. ') : safeToString(explanation.explanation.keyConcepts))}
 className="text-primary hover:text-primary p-1.5 rounded hover:bg-primary/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <ul className="space-y-2">
 {explanation.explanation.keyConcepts.map((concept, index) => (
 <li key={index} className="flex items-start gap-2 text-sm text-muted">
 <span className="text-primary mt-1">•</span>
 <span>{safeToString(concept)}</span>
 </li>
 ))}
 </ul>
 </div>
 )}

 {/* Subtopic Explanations */}
 {explanation.explanation.subtopicExplanations && Object.keys(explanation.explanation.subtopicExplanations).length > 0 && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <ClipboardDocumentListIcon className="h-5 w-5" />
 Detailed Subtopic Explanations
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(Object.values(explanation.explanation.subtopicExplanations || {}).map(safeToString).join('. '))}
 className="text-primary hover:text-primary p-1.5 rounded hover:bg-primary/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <div className="space-y-4">
 {Object.entries(explanation.explanation.subtopicExplanations).map(([subtopic, explanationText], index) => (
 <div key={index} className="rounded-lg border border-secondary/10 bg-bgMain/20 p-4">
 <div className="flex items-center justify-between mb-2">
 <h5 className="text-sm font-semibold text-gray-100">{safeToString(subtopic)}</h5>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanationText))}
 className="text-primary hover:text-primary p-1 rounded hover:bg-primary/10"
 title="Listen to this subtopic"
 >
 <SpeakerWaveIcon className="h-4 w-4" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">{safeToString(explanationText)}</p>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Practical Examples */}
 {explanation.explanation.practicalExamples && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
 <span>💡</span>
 Practical Examples
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanation.explanation.practicalExamples))}
 className="text-primary hover:text-primary p-1.5 rounded hover:bg-primary/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.practicalExamples)}
 </p>
 </div>
 )}

 {/* Common Mistakes */}
 {explanation.explanation.commonMistakes && (
 <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-red-400 flex items-center gap-2">
 <ExclamationTriangleIcon className="h-5 w-5" />
 Common Mistakes to Avoid
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanation.explanation.commonMistakes))}
 className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-red-400/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.commonMistakes)}
 </p>
 </div>
 )}

 {/* Best Practices */}
 {explanation.explanation.bestPractices && (
 <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-4">
 <div className="flex items-center justify-between mb-3">
 <h4 className="text-lg font-semibold text-green-400 flex items-center gap-2">
 <CheckCircleIcon className="h-5 w-5" />
 Best Practices
 </h4>
 {!isSpeaking && (
 <button
 onClick={() => speakText(safeToString(explanation.explanation.bestPractices))}
 className="text-green-400 hover:text-green-300 p-1.5 rounded hover:bg-green-400/10"
 title="Listen to this section"
 >
 <SpeakerWaveIcon className="h-5 w-5" />
 </button>
 )}
 </div>
 <p className="text-sm text-muted leading-relaxed whitespace-pre-line">
 {safeToString(explanation.explanation.bestPractices)}
 </p>
 </div>
 )}
 </div>
 )}

 {/* Loading State */}
 {explanation?.loading && (
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-8">
 <div className="text-center">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
 <h4 className="text-lg font-semibold text-primary mb-2">Generating AI Explanation</h4>
 <p className="text-sm text-muted">
 Creating a comprehensive explanation for <strong>{task.topic}</strong>...
 </p>
 </div>
 </div>
 )}

 {/* Fallback UI */}
 {!explanation?.explanation && !explanation?.loading && (
 <div className="space-y-4">
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <div className="text-center py-8 space-y-4">
 <BookOpenIcon className="h-12 w-12 text-primary mx-auto mb-4" />
 <h4 className="text-lg font-semibold text-primary mb-2">Ready to Learn?</h4>
 <p className="text-sm text-muted mb-4">
 Generate a comprehensive AI-powered explanation of <strong>{task.topic}</strong>
 tailored specifically for your experience level and course.
 </p>
 
 {/* Language selector removed - always English */}

 <button
 onClick={explanation?.onGenerate}
 className="rounded-xl bg-primary text-white px-6 py-3 text-sm font-semibold hover:bg-primary flex items-center gap-2 mx-auto"
 >
 <BookOpenIcon className="h-5 w-5" />
 Generate Topic Explanation {explanationUsage ? ` (${explanationUsage.explanations_remaining} left)` : ''}
 </button>
 </div>
 </div>

 {/* Basic topic information as fallback */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-4">
 <h4 className="text-lg font-semibold text-primary mb-3 flex items-center gap-2">
 <span>📖</span>
 Basic Topic Information
 </h4>
 <div className="space-y-3">
 {task.description && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Description</h5>
 <p className="text-sm text-muted leading-relaxed">{task.description}</p>
 </div>
 )}

 {task.useCase && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Use Case</h5>
 <p className="text-sm text-muted leading-relaxed">{task.useCase}</p>
 </div>
 )}

 {task.expectedOutput && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Expected Output</h5>
 <p className="text-sm text-muted leading-relaxed">{task.expectedOutput}</p>
 </div>
 )}

 {task.subtopics && task.subtopics.length > 0 && (
 <div>
 <h5 className="text-sm font-medium text-gray-200 mb-2">Subtopics ({task.subtopics.length})</h5>
 <ul className="list-disc pl-5 space-y-1 text-sm text-muted">
 {task.subtopics.slice(0, 10).map((subtopic, index) => (
 <li key={index}>{subtopic}</li>
 ))}
 {task.subtopics.length > 10 && (
 <li className="text-primary">... and {task.subtopics.length - 10} more subtopics</li>
 )}
 </ul>
 </div>
 )}
 </div>
 </div>
 </div>
 )}
 </div>
 )}

 {tab ==="video" && (
 <div className="space-y-6">
 {/* Video Generation Section */}
 <div className="rounded-xl border border-secondary/20 bg-bgMain/40 p-6">
 <div className="flex items-center gap-3 mb-4">
 <VideoCameraIcon className="h-6 w-6 text-primary" />
 <h4 className="text-lg font-semibold text-primary">🎬 AI Tutorial Video Generator</h4>
 </div>
 
 <div className="space-y-4">
 {/* Language selector removed - always English */}
 {/* Video Info Card */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="bg-primary/20 p-2 rounded-lg">
                  <VideoCameraIcon className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h5 className="font-semibold text-primary mb-1">What You'll Get</h5>
                  <p className="text-sm text-heading mb-2">
                    A comprehensive tutorial video for <strong className="text-primary">{task.topic}</strong>
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-muted">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-primary rounded-full"></span>
                      Duration: 60-90 seconds
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-primary rounded-full"></span>
                      HD Quality (1280x720)
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-primary rounded-full"></span>
                      {task.subtopics?.length || 0} subtopics covered
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-primary rounded-full"></span>
                      Code examples included
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!video?.url && video?.status !== "queued" ? (
              <div className="space-y-4">
                {/* Enhanced Generate Button */}
                <button
                  onClick={video?.onGenerate}
                  disabled={video?.generating}
                  className="w-full bg-gradient-to-r from-primary to-secondary hover:shadow-lg disabled:opacity-50 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl disabled:shadow-none"
                >
                  {video?.generating ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                      <span>Generating Your Tutorial...</span>
                    </>
                  ) : (
                    <>
                      <VideoCameraIcon className="h-6 w-6" />
                      <span>Generate Tutorial Video {videoUsage ? ` (${videoUsage.videos_remaining} left)` : ''}</span>
                    </>
                  )}
                </button>
 
 {/* Progress Info */}
 {video?.generating && (
 <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-3">
 <div className="animate-pulse bg-blue-500 w-3 h-3 rounded-full"></div>
 <span className="font-semibold text-blue-400">Creating Your Tutorial Video</span>
 </div>
 <div className="space-y-2 text-sm text-muted">
 <div className="flex justify-between">
 <span>Topic:</span>
 <span className="text-primary font-medium">{task.topic}</span>
 </div>
 <div className="flex justify-between">
 <span>Estimated Time:</span>
 <span className="text-green-400">1-2 minutes</span>
 </div>
 <div className="flex justify-between">
 <span>Content:</span>
 <span className="text-blue-400">{task.subtopics?.length || 0} subtopics + examples</span>
 </div>
 </div>
 <div className="mt-3 text-xs text-muted">
 💡 Please don't close this window while generating...
 </div>
 </div>
 )}
 
 {/* Features List */}
 {!video?.generating && (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Professional narration</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Visual slides & code</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Real-world examples</span>
 </div>
 <div className="flex items-center gap-2 text-muted">
 <CheckCircleIcon className="h-4 w-4 text-green-400" />
 <span>Best practices</span>
 </div>
 </div>
 )}
 </div>
 ) : (
 <div className="space-y-4">
 {/* Success Message */}
 <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
 <div className="flex items-center gap-3 mb-2">
 <div className="bg-green-500/20 p-2 rounded-lg">
 <CheckCircleIcon className="h-5 w-5 text-green-400" />
 </div>
 <div>
 <h5 className="font-semibold text-green-400">
 {video?.status ==="ready" ?"🎉 Video Generated Successfully!" :"⏳ Video rendering in progress"}
 </h5>
 <p className="text-sm text-muted">
 {video?.status ==="ready"
 ?"Your tutorial video is ready to watch"
 : video?.message ||"We're refreshing the player automatically until the MP4 is ready."}
 </p>
 </div>
 </div>
 </div>
 
 {/* Video Player */}
 <div className="space-y-3">
 <div className="relative bg-bgMain rounded-lg overflow-hidden shadow-lg">
 {video?.url ? (
 <>
 <video
 controls
 className="w-full rounded-lg"
 style={{ maxHeight: '400px' }}
 poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4MCIgaGVpZ2h0PSI3MjAiIHZpZXdCb3g9IjAgMCAxMjgwIDcyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEyODAiIGhlaWdodD0iNzIwIiBmaWxsPSIjMUYyOTM3Ii8+CjxjaXJjbGUgY3g9IjY0MCIgY3k9IjM2MCIgcj0iNDAiIGZpbGw9IiNGQkJGMjQiLz4KPHBhdGggZD0iTTYyMCAzNDBMMjYwIDM0MEwyNjAgMzgwTDYyMCAzODBMNjIwIDM0MFoiIGZpbGw9IiMxRjI5MzciLz4KPC9zdmc+"
 onCanPlay={video?.onLoaded}
 onLoadedData={video?.onLoaded}
 onError={video?.onError}
 >
 <source src={getProxiedVideoUrl(video.url)} type="video/mp4" />
 Your browser does not support the video tag.
 </video>
 {video?.status !=="ready" && (
 <div className="absolute inset-0 bg-bgMain/70 flex flex-col items-center justify-center text-center p-4">
 <ArrowPathIcon className="h-8 w-8 text-primary animate-spin mb-3" />
 <p className="text-sm text-gray-200">
 {video?.message ||"Waiting for the video to finish rendering…"}
 </p>
 </div>
 )}
 </>
 ) : (
 <div className="p-6 text-center text-sm text-muted">
 Video URL not available yet. Generate or wait for the rendering process to finish.
 </div>
 )}
 </div>
 {video?.error && (
 <div className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-lg p-3">
 {video.error}
 </div>
 )}
 
 {/* Action Buttons */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
 <button
 onClick={() => window.open(video.url, '_blank')}
 disabled={video?.status !=="ready"}
 className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
 title={video?.status ==="ready" ?"Open video in a new tab" :"Video still rendering"}
 >
 <ArrowTopRightOnSquareIcon className="h-5 w-5" />
 Open in New Tab
 </button>
 <button
 onClick={async () => {
 try {
 const response = await fetch(video.url);
 const blob = await response.blob();
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = url;
 link.download = `tutorial-${task.topic.replace(/[^a-zA-Z0-9]/g, '-')}.mp4`;
 document.body.appendChild(link);
 link.click();
 document.body.removeChild(link);
 window.URL.revokeObjectURL(url);
 } catch (err) {
 console.error('Download failed:', err);
 alert('Download failed. Please try opening in a new tab instead.');
 }
 }}
 disabled={video?.status !=="ready"}
 className="bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
 title={video?.status ==="ready" ?"Download MP4" :"Video still rendering"}
 >
 <ArrowDownTrayIcon className="h-5 w-5" />
 Download Video
 </button>
 </div>
 
 {/* Regenerate Button */}
 <button
 onClick={video?.onGenerate}
 className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
 >
 <ArrowPathIcon className="h-4 w-4" />
 Regenerate Video
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 )}

      {tab === "submit" && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* AI Guidance Header */}
      <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="text-2xl bg-white p-2 rounded-full shadow-sm">🤖</div>
          <div className="flex-1 pt-1">
            <h4 className="text-sm font-bold text-primary mb-1">AI Mentor Guidance</h4>
            <p className="text-sm text-zinc-700">{evidenceInfo?.instructions || "Based on the expected output, I've prepared the following evidence fields for you to complete."}</p>
            {evidenceInfo?.requirements && (
              <div className="mt-3 flex flex-wrap gap-2">
                {evidenceInfo.requirements.map((req, i) => (
                  <span key={i} className="text-[10px] px-2 py-1 rounded-full bg-white text-zinc-600 font-medium border border-zinc-200 shadow-sm flex items-center gap-1">
                    {req.icon} {req.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

          {/* Dynamic Evidence Fields */}
          <div className="space-y-6">
            {/* GitHub Section */}
            {(githubRepoUrl || githubStatus?.repo_url || evidenceInfo?.requiresGithub || submissionType === 'github') && (
              <div className="rounded-xl border border-primary/40 bg-primary/10 p-4">
                <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                  <GitHubIcon className="h-4 w-4" /> GitHub Repository
                </h4>
                {(githubRepoUrl || githubStatus?.repo_url) ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted font-bold">Push your latest changes to your dedicated repository:</p>
                    <div className="flex items-center gap-3">
                      <code className="bg-bgMain/60 px-2 py-1 rounded text-xs text-primary border border-primary/20 flex-1 truncate font-mono">
                        {githubRepoUrl || githubStatus?.repo_url}
                      </code>
                      <div className="flex gap-2">
                        <button 
                          onClick={syncInvitationStatus}
                          className="text-xs bg-bgMain/40 text-primary border border-primary/20 px-3 py-1 rounded hover:bg-primary/10 transition flex items-center gap-1 font-bold"
                          title="Refresh status"
                        >
                          <ArrowPathIcon className="h-3 w-3" />
                        </button>
                        <a href={githubRepoUrl || githubStatus?.repo_url} target="_blank" rel="noopener noreferrer" className="text-xs bg-primary text-white px-3 py-1 rounded hover:bg-secondary transition flex items-center gap-1 font-bold">
                          <ArrowTopRightOnSquareIcon className="h-3 w-3" /> Open Repo
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-zinc-600 italic">No repository provisioned. Please contact support if you enrolled with a GitHub username.</p>
                      <button 
                        onClick={syncInvitationStatus}
                        className="text-xs bg-primary/10 text-primary px-3 py-1 rounded hover:bg-primary/20 transition flex items-center gap-1 font-bold"
                      >
                        <ArrowPathIcon className="h-3 w-3" /> Check Status
                      </button>
                    </div>
                    <label className="block mt-4">
                      <span className="text-xs text-muted mb-1 block font-bold">OR: Provide your own repository URL</span>
                      <input type="url" className="w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3" placeholder="https://github.com/..." value={githubUrl} onChange={(e)=>setGithubUrl(e.target.value)} />
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* File Uploads (Screenshots/Docs) */}
            {(evidenceInfo?.fields?.includes('screenshot') || evidenceInfo?.fields?.includes('document') || submissionType === 'files') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-xl bg-primary/5 border border-primary/20">
                <Uploader label="Screenshot/Image" accept="image/*" file={imgFile} setFile={setImgFile} />
                <Uploader label="Documentation (PDF/MD)" accept=".pdf,.md,.doc,.docx" file={docFile} setFile={setDocFile} />
              </div>
            )}

            {/* Database / SQL Section */}
            {(evidenceInfo?.fields?.includes('sqlScript') || evidenceInfo?.fields?.includes('queryOutput') || evidenceInfo?.fields?.includes('commands') || evidenceInfo?.fields?.includes('sql_script') || evidenceInfo?.fields?.includes('query_output')) && (
              <div className="space-y-4 p-4 rounded-xl bg-blue-500/5 border border-blue-500/20">
                <h4 className="text-sm font-bold text-blue-600 flex items-center gap-2">
                  <CircleStackIcon className="h-5 w-5" /> Database Evidence
                </h4>
                {(evidenceInfo?.fields?.includes('sqlScript') || evidenceInfo?.fields?.includes('sql_script')) && (
                  <label className="block">
                    <span className="text-xs text-muted mb-1 block">SQL / Database Script</span>
                    <textarea className="w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 font-mono" rows={4} placeholder="CREATE TABLE... INSERT INTO..." value={sqlScript} onChange={(e)=>setSqlScript(e.target.value)} />
                  </label>
                )}
                {(evidenceInfo?.fields?.includes('queryOutput') || evidenceInfo?.fields?.includes('query_output')) && (
                  <label className="block">
                    <span className="text-xs text-muted mb-1 block">Query Output / Results</span>
                    <textarea className="w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 font-mono" rows={3} placeholder="Rows returned: 5..." value={queryOutput} onChange={(e)=>setQueryOutput(e.target.value)} />
                  </label>
                )}
                {evidenceInfo?.fields?.includes('commands') && (
                  <label className="block">
                    <span className="text-xs text-muted mb-1 block">MongoDB / Shell Commands</span>
                    <textarea className="w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3 font-mono" rows={3} placeholder="db.collection.find()..." value={commands} onChange={(e)=>setCommands(e.target.value)} />
                  </label>
                )}
              </div>
            )}

            {/* Terminal Section */}
            {(evidenceInfo?.fields?.includes('terminalOutput') || evidenceInfo?.fields?.includes('terminal_output')) && (
              <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-700">
                <h4 className="text-sm font-bold text-green-500 flex items-center gap-2 mb-3">
                  <CommandLineIcon className="h-5 w-5" /> Terminal Output
                </h4>
                <textarea className="w-full rounded-lg bg-black/40 border border-zinc-800 text-sm text-green-400 p-3 font-mono" rows={5} placeholder="Paste your terminal logs here..." value={terminalOutput} onChange={(e)=>setTerminalOutput(e.target.value)} />
              </div>
            )}

            {/* Notes Section */}
            <label className="block">
              <span className="text-xs text-muted">Additional Notes for Mentor (optional)</span>
              <textarea className="mt-1 w-full rounded-lg bg-bgMain/40 border border-zinc-700 text-sm text-gray-200 p-3" rows={2} placeholder="Anything you'd like to highlight..." value={comments} onChange={(e)=>setComments(e.target.value)} />
            </label>
          </div>

          {serverError && <div className="text-xs text-red-400 flex items-center gap-2 bg-red-400/10 p-3 rounded-lg border border-red-400/20"><ExclamationTriangleIcon className="h-4 w-4"/> {serverError}</div>}
          
          <div className="flex justify-end pt-4">
            <button
              onClick={() => onSubmit({ 
                docFile, 
                imgFile, 
                comments, 
                githubUrl, 
                submissionType, 
                setSubmitting, 
                setServerError,
                sqlScript,
                queryOutput,
                commands,
                terminalOutput,
                evidenceInfo // Pass evidenceInfo to ensure it's available in handleVerifyMyWork if needed via closure or param
              })}
              disabled={submitting || (evidenceInfo?.requiresGithub && !githubRepoUrl && !githubUrl)}
              className="rounded-xl bg-primary text-white px-6 py-3 text-sm font-bold hover:bg-secondary transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-primary/20"
            >
              {submitting ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <CheckCircleIcon className="h-5 w-5" />}
              {submitting ? "Verifying..." : "Verify My Work"}
            </button>
          </div>
        </div>
      )}

                  {tab === "github" && (
                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300 space-y-6">
                      <div className={`p-6 rounded-2xl border ${isDark ? "bg-bgSection border-accentGray/20" : "bg-gray-50 border-gray-200"}`}>
                        <h4 className="font-bold mb-4 flex items-center gap-2">
                          <GitHubIcon className="h-5 w-5" /> GitHub Repository
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <p className="text-sm font-bold text-muted">Repository Details</p>
                            <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-white border"}`}>
                              <p className="text-xs text-muted mb-1">Repository Name</p>
                              <p className="font-mono text-sm">{githubStatus.repo_name}</p>
                              <a href={githubStatus.repo_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary font-bold mt-2 inline-block">View on GitHub ↗</a>
                            </div>
                            <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-white border"}`}>
                              <p className="text-xs text-muted mb-1">Invitation Status</p>
                              <span className={`px-2 py-1 rounded text-xs font-bold ${
                                githubStatus.invite_status === 'accepted' ? 'bg-green-500/10 text-green-500' : 
                                githubStatus.invite_status === 'pending' ? 'bg-amber-500/10 text-amber-500' : 'bg-gray-500/10 text-gray-500'
                              }`}>
                                {githubStatus.invite_status?.toUpperCase()}
                              </span>
                              {githubStatus.invite_status === 'pending' && (
                                <p className="text-xs text-amber-500 mt-2">Please accept your GitHub invitation to continue project-based tasks.</p>
                              )}
                            </div>
                          </div>
                          <div className="space-y-4">
                            <p className="text-sm font-bold text-muted">Sync Status</p>
                            <div className={`p-4 rounded-xl ${isDark ? "bg-accentGray/5" : "bg-white border"}`}>
                              <p className="text-xs text-muted mb-1">Last Synced</p>
                              <p className="text-sm">{githubStatus.updated_at ? new Date(githubStatus.updated_at).toLocaleString() : "—"}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {tab ==="feedback" && (
                    !reviewState ? (
                      <p className="text-sm text-muted">No submission yet. Use the “Verify My Work” tab.</p>
                    ) : reviewState.status ==="pending" ? (
                      <p className="text-sm text-primary flex items-center gap-2"><ArrowPathIcon className="h-4 w-4 animate-spin"/> Verification in progress — AI is reviewing your work…</p>
                    ) : (
                      <div className="space-y-6">
                        <div className={`rounded-xl border p-4 ${reviewState.status === "passed" ? "border-green-500/40 bg-green-500/10" : "border-red-500/40 bg-red-500/10"}`}>
                          <div className={`font-semibold flex items-center gap-2 ${reviewState.status === "passed" ? "text-green-400" : "text-red-400"}`}>
                            {reviewState.status === "passed" ? <CheckCircleIcon className="h-5 w-5" /> : <ExclamationTriangleIcon className="h-5 w-5" />}
                            {reviewState.status === "passed" ? "Passed" : "Changes Needed"}
                          </div>
                          {"score" in reviewState && reviewState.score != null && (
                            <div className="text-xs text-muted mt-1">Mastery Score: {(reviewState.score * 100).toFixed(0)}%</div>
                          )}
                          <p className="text-sm text-gray-200 mt-2 whitespace-pre-line">{reviewState.feedback}</p>
                        </div>

                        {reviewState.details && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {reviewState.details.strengths && reviewState.details.strengths.length > 0 && (
                              <div className="p-4 rounded-xl border border-green-500/20 bg-green-500/5">
                                <h5 className="text-xs font-bold uppercase text-green-400 mb-2">Strengths</h5>
                                <ul className="list-disc pl-4 text-xs text-gray-300 space-y-1">
                                  {reviewState.details.strengths.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                              </div>
                            )}
                            {reviewState.details.weaknesses && reviewState.details.weaknesses.length > 0 && (
                              <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5">
                                <h5 className="text-xs font-bold uppercase text-red-400 mb-2">Weaknesses</h5>
                                <ul className="list-disc pl-4 text-xs text-gray-300 space-y-1">
                                  {reviewState.details.weaknesses.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                              </div>
                            )}
                            {reviewState.details.improvementSuggestions && reviewState.details.improvementSuggestions.length > 0 && (
                              <div className="p-4 rounded-xl border border-blue-500/20 bg-blue-500/5 md:col-span-2">
                                <h5 className="text-xs font-bold uppercase text-blue-400 mb-2">Improvement Suggestions</h5>
                                <ul className="list-disc pl-4 text-xs text-gray-300 space-y-1">
                                  {reviewState.details.improvementSuggestions.map((s, i) => <li key={i}>{s}</li>)}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {reviewState.status !== "passed" && (
                          <div className="text-center pt-4">
                            <button 
                              onClick={() => setTab("submit")}
                              className="text-sm font-bold text-primary hover:underline"
                            >
                              Resubmit with improvements
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  )}

 {tab ==="visualize" && (
 <div className="space-y-6">
 <div>
 <h3 className="text-lg font-semibold text-white mb-2">AI-Powered Visualization Generator</h3>
 <p className="text-muted">
 Generate task-specific visualizations including architecture diagrams, dashboards, flowcharts, and mockups to help you understand how real-world implementations look.
 </p>
 </div>
 
 <div>
 <h4 className="text-md font-medium text-white mb-4">Visualization Types</h4>
 <div className="grid grid-cols-2 gap-4">
 <button 
 className="bg-bgSection rounded-lg p-4 border border-gray-600 hover:border-primary transition-colors text-left"
 onClick={() => visualize?.setVisualHint(`${visualize?.visualHint || ''} Architecture Diagram: `)}
 >
 <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
 <h5 className="text-white font-medium">Architecture Diagram</h5>
 </button>
 <button 
 className="bg-bgSection rounded-lg p-4 border border-gray-600 hover:border-primary transition-colors text-left"
 onClick={() => visualize?.setVisualHint(`${visualize?.visualHint || ''} Dashboard Mockup: `)}
 >
 <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
 <h5 className="text-white font-medium">Dashboard Mockup</h5>
 </button>
 <button 
 className="bg-bgSection rounded-lg p-4 border border-gray-600 hover:border-primary transition-colors text-left"
 onClick={() => visualize?.setVisualHint(`${visualize?.visualHint || ''} Process Flow: `)}
 >
 <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
 <h5 className="text-white font-medium">Process Flow</h5>
 </button>
 <button 
 className="bg-bgSection rounded-lg p-4 border border-gray-600 hover:border-primary transition-colors text-left"
 onClick={() => visualize?.setVisualHint(`${visualize?.visualHint || ''} UI/UX Design: `)}
 >
 <div className="w-8 h-8 bg-gray-600 rounded mb-2"></div>
 <h5 className="text-white font-medium">UI/UX Design</h5>
 </button>
 </div>
 </div>
 
 <div className="bg-bgSection rounded-lg p-4">
 <h4 className="text-md font-medium text-white mb-2">Task Context (Auto-filled for better results)</h4>
 <div className="space-y-2 text-sm">
 <p><strong className="text-primary">Project:</strong> {task.projectName}</p>
 <p><strong className="text-primary">Use Case:</strong> {task.useCase}</p>
 <p><strong className="text-primary">Expected Output:</strong> {task.expectedOutput}</p>
 </div>
 </div>
 
 <div>
 <label className="block text-sm text-muted mb-2">Custom Visualization Prompt</label>
 <textarea
 value={visualize?.visualHint || ''}
 onChange={(e) => visualize?.setVisualHint(e.target.value)}
 placeholder="Describe what you'd like to visualize..."
 className="w-full bg-bgSection border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-secondary h-24"
 />
 </div>
 
 <div className="flex justify-center">
 <button
 onClick={visualize?.onVisualize}
 disabled={visualize?.visualizing}
 className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
 >
 {visualize?.visualizing ? (
 <>
 <ArrowPathIcon className="h-4 w-4 animate-spin" />
 Generating...
 </>
 ) : (
 <>
 <PhotoIcon className="h-4 w-4" />
 Generate Visualization
 </>
 )}
 </button>
 </div>
 
 <VisualizationPreview
 loading={visualize?.visualizing}
 imageUrl={visualize?.visualUrl}
 taskTitle={task?.title || task?.topic}
 hint={visualize?.visualHint || ''}
 generatedAt={visualize?.visualGeneratedAt}
 downloadFileName={`${slugify(task?.title || task?.topic || 'visual')}-visual.png`}
 />
 </div>
 )}
 </div>
 </div>

 </div>
 );
}

function Uploader({ label, accept, file, setFile }) {
 const fileInputRef = useRef(null);
 
 const handleFileSelect = (e) => {
 setFile(e.target.files?.[0] || null);
 };

 return (
 <div className="block">
 <span className="text-xs text-muted">{label}</span>
 <div className="mt-1 flex items-center gap-2">
 <input 
 ref={fileInputRef}
 type="file" 
 accept={accept} 
 onChange={handleFileSelect} 
 className="hidden"
 />
 <button
 type="button"
 onClick={() => fileInputRef.current?.click()}
 className="bg-primary text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-primary transition-colors"
 >
 Choose File
 </button>
 {file ? (
 <span className="text-xs text-muted truncate max-w-[12rem]">{file.name}</span>
 ) : (
 <span className="text-xs text-muted">No file chosen</span>
 )}
 </div>
 </div>
 );
}
function MentorModal({ onClose, technology, task, explanation }) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`w-full bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 ${isFullscreen ? 'h-full max-w-none' : 'max-w-5xl h-[85vh]'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-primary/10 rounded-2xl">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">AI Topic Explainer</h3>
              <p className="text-xs text-muted font-medium uppercase tracking-wider">{technology} • {task?.title || task?.topic}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2.5 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-5 w-5" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5" />
              )}
            </button>
            <button 
              className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all" 
              onClick={onClose}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-gray-50/30">
          {!explanation?.explanation ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
              <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
              <p className="text-gray-500 font-medium">Analyzing topic and generating detailed explanation...</p>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-10">
              {/* Main Overview */}
              <section className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
                <h4 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <BookOpenIcon className="h-7 w-7 text-primary" />
                  Detailed Overview
                </h4>
                <div className="prose prose-blue max-w-none">
                  <p className="text-gray-700 leading-relaxed text-lg whitespace-pre-line">
                    {safeToString(explanation.explanation.topicOverview)}
                  </p>
                </div>
              </section>

              {/* Key Concepts Grid */}
              {explanation.explanation.keyConcepts?.length > 0 && (
                <section>
                  <h4 className="text-xl font-bold text-gray-900 mb-6 px-2">Key Concepts to Master</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {explanation.explanation.keyConcepts.map((concept, i) => (
                      <div key={i} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:border-primary/30 transition-colors">
                        <div className="w-10 h-10 bg-primary/5 rounded-xl flex items-center justify-center text-primary font-bold mb-4">
                          {i + 1}
                        </div>
                        <p className="text-gray-800 font-medium leading-relaxed">{concept}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Practical Examples */}
              {explanation.explanation.practicalExamples && (
                <section className="bg-gray-900 rounded-3xl p-8 shadow-xl">
                  <h4 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                    <CodeBracketIcon className="h-6 w-6 text-primary" />
                    Practical Implementation
                  </h4>
                  <div className="bg-black/40 rounded-2xl p-6 font-mono text-sm text-blue-100 overflow-x-auto border border-white/5">
                    <pre className="whitespace-pre-wrap">{safeToString(explanation.explanation.practicalExamples)}</pre>
                  </div>
                </section>
              )}

              {/* Best Practices & Mistakes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {explanation.explanation.bestPractices && (
                  <section className="bg-green-50/50 rounded-3xl p-8 border border-green-100">
                    <h4 className="text-lg font-bold text-green-800 mb-4 flex items-center gap-2">
                      <CheckCircleIcon className="h-5 w-5" />
                      Best Practices
                    </h4>
                    <p className="text-green-700 text-sm leading-relaxed whitespace-pre-line">
                      {safeToString(explanation.explanation.bestPractices)}
                    </p>
                  </section>
                )}
                {explanation.explanation.commonMistakes && (
                  <section className="bg-red-50/50 rounded-3xl p-8 border border-red-100">
                    <h4 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
                      <ExclamationTriangleIcon className="h-5 w-5" />
                      Common Pitfalls
                    </h4>
                    <p className="text-red-700 text-sm leading-relaxed whitespace-pre-line">
                      {safeToString(explanation.explanation.commonMistakes)}
                    </p>
                  </section>
                )}
              </div>

              {/* Real World Usage */}
              {explanation.explanation.realWorldUsage && (
                <section className="bg-blue-50/50 rounded-3xl p-8 border border-blue-100">
                  <h4 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                    <GlobeAltIcon className="h-5 w-5" />
                    Real-World Application
                  </h4>
                  <p className="text-blue-700 text-sm leading-relaxed whitespace-pre-line">
                    {safeToString(explanation.explanation.realWorldUsage)}
                  </p>
                </section>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 bg-white flex justify-end">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-primary text-white font-bold rounded-2xl hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all active:scale-95"
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </div>
  );
}



